import React, { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../layouts/PageTitle";
import { Formik } from "formik";
import * as Yup from "yup";
import { Fn_AddEditData, Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { toast } from "react-toastify";
import usePayrollParams from "../utils/usePayrollParams";
import useSession from "../utils/useSession";

const EmployeeSchema = Yup.object().shape({
  Name: Yup.string().required("Employee name is required"),
  F_FirmMaster: Yup.string().required("Firm is required"),
  MobileNo: Yup.string()
    .test("len", "Mobile number cannot exceed 10 digits", (val) => !val || val.length <= 10)
    .matches(/^[0-9]*$/, "Mobile number must contain digits only"),
  Email: Yup.string().email("Invalid email address"),
});

const calculateRetirementDate = (dobString, retirementAge = 58) => {
  const age = Number(retirementAge) > 0 ? Number(retirementAge) : 58;
  if (!dobString) return "";
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return "";
  dob.setFullYear(dob.getFullYear() + age);
  const yyyy = dob.getFullYear();
  const mm = String(dob.getMonth() + 1).padStart(2, "0");
  const dd = String(dob.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const calculateAge = (dobString) => {
  if (!dobString) return "";
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age >= 0 ? age : "";
};

const emptyDeptRow           = () => ({ F_DepartmentMaster: "", F_DesignationMaster: "", EffectiveDate: "" });
const emptyShiftRow          = () => ({ F_ShiftMaster: "", EffectiveFrom: "" });
const emptyJoinRow           = () => ({ JoinDate: "", F_JoinTypeMaster: "", LeftDate: "", ReasonOfLeft: "" });
const emptySalaryRow         = (defaultType = 1) => ({ BasicSalary: "", EffectiveDate: "", F_BasicTypeMaster: defaultType });
const emptyNomineeRow        = () => ({ NomineeName: "", Address: "", F_RelationshipMaster: "", BirthDate: "", Amount: "", ParentName: "", AadharNo: "", Photo: "" });
const emptyRelativeRow       = () => ({ RelativeName: "", DateOfBirth: "", Address: "", F_RelationshipMaster: "", LivingWith: 0, ForESI: 0, AadharNo: "", Photo: "" });
const emptyPensionNomineeRow = () => ({ NomineeName: "", Address: "", F_RelationshipMaster: "", BirthDate: "", Amount: "", ParentName: "", AadharNo: "", NameHindi: "", AddressHindi: "", RelHindi: "", Photo: "" });
const emptyMinWagesRow       = () => ({ MinWages: "", EffectiveDate: "" });
const emptyExtraPFRateRow    = () => ({ ExtraPFRate: "", IsManual: false, EffectiveDate: "" });
const emptyAllowanceRow      = () => ({ F_AllowanceMaster: "", Amount: "", Calculation: "MANUAL", IsOnGross: false, IsPFApply: false, IsESIApply: false, IsRoundOffApply: true, IsWorkingDaysApply: false, IsApplicable: true, EffectiveFrom: "" });
const emptyDeductionRow      = () => ({ F_DeductionMaster: "", Amount: "", Calculation: "MANUAL", IsOnGross: false, IsPFApply: false, IsESIApply: false, IsRoundOffApply: true, IsWorkingDaysApply: false, IsApplicable: true, EffectiveFrom: "" });

const AddEdit_EmployeeMaster = () => {
  const [activeTab, setActiveTab] = useState("basicInfo");
  const [maxWorkers, setMaxWorkers] = useState(null);
  const [existingCount, setExistingCount] = useState(0);
  const [retirementAge, setRetirementAge] = useState(58);
  const [isAutoEmpCode, setIsAutoEmpCode] = useState(false);
  const [firmShiftTimeMappings, setFirmShiftTimeMappings] = useState([]);

  // Multi ESI / Multi PF payroll parameters
  const { isMultiESI, isMultiPF, esiNoList, pfNoList } = usePayrollParams();

  // JSON table rows
  const [deptRows,           setDeptRows]           = useState([emptyDeptRow()]);
  const [shiftRows,          setShiftRows]          = useState([emptyShiftRow()]);
  const [joinRows,           setJoinRows]           = useState([emptyJoinRow()]);
  const [salaryRows,         setSalaryRows]         = useState([emptySalaryRow(1)]);
  const [nomineeRows,        setNomineeRows]        = useState([emptyNomineeRow()]);
  const [relativeRows,       setRelativeRows]       = useState([emptyRelativeRow()]);
  const [pensionNomineeRows, setPensionNomineeRows] = useState([emptyPensionNomineeRow()]);
  const [minWagesRows,       setMinWagesRows]       = useState([emptyMinWagesRow()]);
  const [extraPFRateRows,    setExtraPFRateRows]    = useState([emptyExtraPFRateRow()]);
  const [allowanceRows,      setAllowanceRows]      = useState([emptyAllowanceRow()]);
  const [originalAllowanceRows, setOriginalAllowanceRows] = useState([]);
  const [allowanceViewIndex, setAllowanceViewIndex] = useState({});
  const [expandedAllowances, setExpandedAllowances] = useState({});
  // Deduction states
  const [deductionRows,      setDeductionRows]      = useState([emptyDeductionRow()]);
  const [originalDeductionRows, setOriginalDeductionRows] = useState([]);
  const [deductionViewIndex, setDeductionViewIndex] = useState({});
  const [expandedDeductions, setExpandedDeductions] = useState({});

  const { firm, firmId, branch, branchId } = useSession();

  const [state, setState] = useState({
    id: 0,
    formData: {
      F_FirmMaster: firmId ? String(firmId) : "",
      F_BranchMaster: branchId ? String(branchId) : "",
      SerialNo: "",
      Name: "",
      FatherName: "",
      EmployeeType: "Ordinary",
      CardNo: "",
      Sex: "M",
      DateOfBirth: "",
      DateOfRetire: "",
      IsDeferredPension: false,
      PensionAge: "",
      BankPayment: true,
      F_ShiftMaster: "",
      PermanentAddress: "",
      PresentAddress: "",
      MobileNo: "",
      Email: "",
      EdQualification: "",
      EducationQualification: "",
      IsHandicap: false,
      HandicapCategory: "",
      Status: "Working",
      AadharNumber: "",
      AadharNo: "",
      NameInAadhar: "",
      MaritalStatus: false,
      Married: "Unmarried",
      RefundFileDate: "",
      Refunded: false,
      GratuityPaidDate: "",
      F_DepartmentMaster: "",
      F_DesignationMaster: "",
      DepartmentName: "",
      DesignationName: "",
      Remark: "",
      // ESI
      ESIApplicable: false, ESINo: "", ESIJoiningDate: "", ESIOldEmployee: false,
      TICs: false, PICs: false, Accident: false,
      // PF
      PFApplicable: false, EmplrPFOverLimit: false, EmpleeDedOverLimit: false,
      PensionApplicable: true, PensionOverLimit: false, HigherPension: false,
      PFNo: "", UAN: "", PFJoinDate: "", ExtraPF: "", PFCalculationType: "Percentage",
      OldMemberPF: false, OldMemberPension: false, PrevPFNo: "", OldUAN: "", DateOfExitPr: "",
      PMRPY: false, PMGKY: false, ABRY: false, FixedPFSalary: "",
      // Weekly Holiday
      WeeklyHoliday: "Sunday",
      // Bank
      BankName: "", BankBranch: "", IFSCCode: "", BankAccountNo: "", NameAsBank: "",
      // Documents
      PanCardNo: "", NameAsPerPan: "",
      DrivingLicenceNo: "", DLExpiryDate: "", NameAsPerDL: "",
      PassportNo: "", PassportFrom: "", PassportTill: "", PassportName: "",
      IsInternationalEmployee: false, CountryOrigin: "", Nationality: "",
      F_CountryMaster: 1,
      // KYC Fields
      VoterIDNo: "", NameAsPerVoterID: "",
      RationCardNo: "", NameAsPerRationCard: "",
      NationalPopulationNo: "", NameAsPerNPR: "",
      KYCEmployeeType: "",
      F_KYCEmployeeTypeMaster: "",
      OpeningBalanceSalary: "",
      // Pension Nominee & Opening Balance Fields
      EmployeePartOpBal: "", EmployerPartOpBal: "", OpBalYear: "", Year: "",
      Photo: "",
      PhotoFile: null,
      PhotoPreview: "",
      Signature: "",
      SignatureFile: null,
      SignaturePreview: "",
      photoSignatureJson: "",
    },
    FirmMaster:        [],
    BranchMaster:      [],
    DepartmentMaster:  [],
    DesignationMaster: [],
    ShiftMaster:       [],
    EmployeeTypeMaster:[],
    JoinTypeMaster:    [],
    BasicTypeMaster:   [],
    RelationshipMaster:[],
    AllowanceMaster:   [],
    DeductionMaster:   [],
    CountryMaster:     [],
    isProgress: false,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const API_URL_SAVE = "EmployeeMaster/0/token";
  const API_URL_EDIT = `${API_WEB_URLS.MASTER}/0/token/EmployeeDataDynamic`;

  // Helper to filter shifts based on selected Firm & Branch
  const getBranchShifts = (selectedBranchId, selectedFirmId) => {
    const fId = selectedFirmId || firmId;
    const bId = selectedBranchId || branchId;

    if (firmShiftTimeMappings && firmShiftTimeMappings.length > 0) {
      let filtered = firmShiftTimeMappings;

      if (bId) {
        filtered = filtered.filter(m => String(m.F_BranchMaster) === String(bId));
      } else if (fId) {
        filtered = filtered.filter(m => String(m.F_FirmMaster) === String(fId));
      }

      if (filtered.length > 0) {
        return filtered.map(m => {
          const shiftId = m.F_ShiftMaster || m.Id;
          const matchInShiftMaster = (state.ShiftMaster || []).find(s => String(s.Id) === String(shiftId));
          const timeText = (m.InTime && m.OutTime) ? `${m.InTime} - ${m.OutTime}` : "";
          const name = matchInShiftMaster?.Name
            ? (timeText ? `${matchInShiftMaster.Name} (${timeText})` : matchInShiftMaster.Name)
            : (m.Name || (timeText ? `Shift (${timeText})` : `Shift #${shiftId}`));

          return {
            Id: shiftId,
            Name: name,
            InTime: m.InTime,
            OutTime: m.OutTime,
            F_BranchMaster: m.F_BranchMaster,
            F_FirmMaster: m.F_FirmMaster
          };
        });
      }
    }

    return state.ShiftMaster || [];
  };

  const checkMaxWorkers = (fId, recordId = 0) => {
    if (!fId) {
      setMaxWorkers(null);
      setExistingCount(0);
      setRetirementAge(58);
      setIsAutoEmpCode(false);
      return;
    }
    
    Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${fId}`)
      .then(resParams => {
        const param = resParams?.[0];
        const age = param?.RetirementAge;
        setRetirementAge(age !== undefined && age !== null && Number(age) > 0 ? Number(age) : 58);
        
        const autoGen = !!param?.IsAuto;
        setIsAutoEmpCode(autoGen);

        Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/GetMaxWorkersByFirmId%20/Id/${fId}`)
          .then(res => {
            const max = res?.[0]?.MaxWorkers;
            const maxNum = max !== undefined && max !== null ? Number(max) : null;
            setMaxWorkers(maxNum);
            
            Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/EmployeeDataAll/Id/${fId}`)
              .then(resEmps => {
                const count = resEmps ? resEmps.length : 0;
                setExistingCount(count);
                if (recordId === 0 && maxNum !== null && maxNum > 0 && count >= maxNum) {
                  alert(`Warning: Firm's limit of ${maxNum} workers has been reached. You will not be able to save this employee.`);
                }
              })
              .catch(() => setExistingCount(0));
          })
          .catch(() => setMaxWorkers(null));
      })
      .catch(() => {
        setRetirementAge(58);
        setIsAutoEmpCode(false);
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      Fn_FillListData(dispatch, setState, "FirmMaster",        `${API_WEB_URLS.MASTER}/0/token/FirmMaster/Id/0`);
      if (firmId) {
        Fn_FillListData(dispatch, setState, "BranchMaster", `${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${firmId}`);
      }
      Fn_FillListData(dispatch, setState, "DepartmentMaster",  `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/TBL.F_FirmMaster/${firmId || 0}`);
      Fn_FillListData(dispatch, setState, "DesignationMaster", `${API_WEB_URLS.MASTER}/0/token/DesignationMaster/TBL.F_FirmMaster/${firmId || 0}`);
      Fn_FillListData(dispatch, setState, "ShiftMaster",       `${API_WEB_URLS.MASTER}/0/token/ShiftMaster/Id/0`);
      Fn_FillListData(dispatch, setState, "EmployeeTypeMaster", `${API_WEB_URLS.MASTER}/0/token/EmployeeTypeMaster/Id/0`);
      Fn_FillListData(dispatch, setState, "JoinTypeMaster",    `${API_WEB_URLS.MASTER}/0/token/JoinTypeMaster/Id/0`);
      Fn_FillListData(dispatch, setState, "BasicTypeMaster",   `${API_WEB_URLS.MASTER}/0/token/BasicTypeMaster/Id/0`)
        .then(resTypes => {
          if (resTypes && resTypes.length > 0) {
            const monthly = resTypes.find(b => b.Id === 1 || String(b.Name).toLowerCase().includes("month"));
            const defaultId = monthly ? monthly.Id : resTypes[0].Id;
            setSalaryRows(prev => prev.map(r => (!r.F_BasicTypeMaster ? { ...r, F_BasicTypeMaster: defaultId } : r)));
          }
        });
      Fn_FillListData(dispatch, setState, "RelationshipMaster",`${API_WEB_URLS.MASTER}/0/token/RelationshipMaster/Id/0`);
      Fn_FillListData(dispatch, setState, "AllowanceMaster",   `${API_WEB_URLS.MASTER}/0/token/AllowanceMasterForEmployeeEntry/TBL.F_FirmMaster/${firmId || 0}`);
      Fn_FillListData(dispatch, setState, "DeductionMaster",   `${API_WEB_URLS.MASTER}/0/token/DeductionMaster/Id/0`);
      Fn_FillListData(dispatch, setState, "CountryMaster",     `${API_WEB_URLS.MASTER}/0/token/CountryMaster/Id/0`);

      // Fetch Firm Shift Time Mappings for branch filtering
      try {
        const mappingRes = await Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/FirmShiftTimeData/Id/0`);
        let parsedMappings = [];
        if (mappingRes && Array.isArray(mappingRes) && mappingRes.length > 0) {
          const jsonStr = mappingRes[0]?.FirmShiftTimeJson;
          if (jsonStr) {
            try {
              parsedMappings = typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
            } catch (err) {
              console.error("Error parsing FirmShiftTimeJson:", err);
              parsedMappings = [];
            }
          } else if (Array.isArray(mappingRes[0])) {
            parsedMappings = mappingRes[0];
          } else if (mappingRes[0]?.F_FirmMaster) {
            parsedMappings = mappingRes;
          }
        }
        setFirmShiftTimeMappings(parsedMappings || []);
      } catch (err) {
        console.error("Error fetching FirmShiftTimeData:", err);
      }

      const Id = (location.state && location.state.Id) || 0;
      if (Id > 0) {
        const res = await Fn_FillListData(dispatch, setState, "Data", API_URL_EDIT + "/" + Id + "/" + (firmId || 0));

        if (res && res.length > 0) {
          const d = res[0];
          const tryParse = (val, def) => { try { const p = JSON.parse(val); return Array.isArray(p) && p.length ? p : [def()]; } catch(_) { return [def()]; } };
          if (d.DepartmentJson)     setDeptRows(tryParse(d.DepartmentJson,           emptyDeptRow));
          if (d.ShiftJson)          setShiftRows(tryParse(d.ShiftJson,                emptyShiftRow));
          if (d.JoinJson)           setJoinRows(tryParse(d.JoinJson,                 emptyJoinRow));
          if (d.SalaryJson)         setSalaryRows(tryParse(d.SalaryJson,             emptySalaryRow));
          if (d.NomineeJson)        setNomineeRows(tryParse(d.NomineeJson,           emptyNomineeRow));
          if (d.RelativeJson)       setRelativeRows(tryParse(d.RelativeJson,         emptyRelativeRow));
          if (d.PensionNomineeJson) setPensionNomineeRows(tryParse(d.PensionNomineeJson, emptyPensionNomineeRow));
          if (d.MinWagesJson)       setMinWagesRows(tryParse(d.MinWagesJson,         emptyMinWagesRow));
          if (d.ExtraPFRateJson)    setExtraPFRateRows(tryParse(d.ExtraPFRateJson,   emptyExtraPFRateRow));
          if (d.AllowanceJson) {
            const parsed = tryParse(d.AllowanceJson, emptyAllowanceRow);
            setAllowanceRows(parsed);
            setOriginalAllowanceRows(JSON.parse(JSON.stringify(parsed)));
          }
          if (d.DeductionJson) {
            const parsed = tryParse(d.DeductionJson, emptyDeductionRow);
            setDeductionRows(parsed);
            setOriginalDeductionRows(JSON.parse(JSON.stringify(parsed)));
          }

          let loadedPhoto = d.Photo || d.EmployeePhoto || "";
          let loadedSignature = d.Signature || "";
          const rawPhotoSigJson = d.photoSignatureJson || d.PhotoSignatureJson || "";

          if (rawPhotoSigJson) {
            try {
              const parsed = typeof rawPhotoSigJson === "string" ? JSON.parse(rawPhotoSigJson) : rawPhotoSigJson;
              const item = Array.isArray(parsed) ? parsed[0] : parsed;
              if (item) {
                if (!loadedPhoto && item.Photo) loadedPhoto = item.Photo;
                if (!loadedSignature && item.Signature) loadedSignature = item.Signature;
              }
            } catch (err) {
              console.error("Error parsing PhotoSignatureJson:", err);
            }
          }

          const activeFirm = d.F_FirmMaster || firmId;
          if (activeFirm) {
            checkMaxWorkers(activeFirm, Id);
          }

          let initialESIDate = d.ESIJoiningDate ? d.ESIJoiningDate.substring(0, 10) : "";
          let initialPFDate = d.PFJoinDate ? d.PFJoinDate.substring(0, 10) : "";
          if ((!initialESIDate || !initialPFDate) && d.DepartmentJson) {
            try {
              const parsedDept = typeof d.DepartmentJson === "string" ? JSON.parse(d.DepartmentJson) : d.DepartmentJson;
              if (Array.isArray(parsedDept) && parsedDept[0]?.EffectiveDate) {
                const effDate = parsedDept[0].EffectiveDate.substring(0, 10);
                if (!initialESIDate) initialESIDate = effDate;
                if (!initialPFDate) initialPFDate = effDate;
              }
            } catch (_) {}
          }

          setState((prev) => ({
            ...prev,
            id: Id,
            formData: {
              F_FirmMaster:        d.F_FirmMaster || firmId || "",
              F_BranchMaster:      d.F_BranchMaster || branchId || "",
              SerialNo:            d.SerialNo || "",
              Name:                d.Name || "",
              FatherName:          d.FatherName || "",
              EmployeeType:        d.EmployeeType || "Ordinary",
              CardNo:              d.CardNo || "",
              Sex:                 d.Sex || "M",
              DateOfBirth:         d.DateOfBirth ? d.DateOfBirth.substring(0, 10) : "",
              DateOfRetire:        d.DateOfRetire ? d.DateOfRetire.substring(0, 10) : "",
              IsDeferredPension:    !!d.IsDeferredPension,
              PensionAge:          d.PensionAge || "",
              BankPayment:         d.BankPayment !== undefined && d.BankPayment !== null ? !!d.BankPayment : true,
              F_ShiftMaster:       d.F_ShiftMaster || "",
              PermanentAddress:    d.PermanentAddress || "",
              PresentAddress:      d.PresentAddress || "",
              MobileNo:            d.MobileNo || "",
              Email:               d.Email || d.EMail || "",
              EdQualification:     d.EdQualification || d.EducationQualification || "",
              EducationQualification: d.EducationQualification || d.EdQualification || "",
              IsHandicap:          !!d.IsHandicap,
              HandicapCategory:    d.HandicapCategory || "",
              Status:              d.Status || "Working",
              AadharNumber:        d.AadharNumber || d.AadharNo || "",
              AadharNo:            d.AadharNo || d.AadharNumber || "",
              NameInAadhar:        d.NameInAadhar || "",
              MaritalStatus:       !!d.MaritalStatus,
              Married:             d.Married || "Unmarried",
              RefundFileDate:      d.RefundFileDate ? d.RefundFileDate.substring(0, 10) : "",
              Refunded:            !!d.Refunded,
              GratuityPaidDate:    d.GratuityPaidDate ? d.GratuityPaidDate.substring(0, 10) : "",
              F_DepartmentMaster:  d.F_DepartmentMaster || "",
              F_DesignationMaster: d.F_DesignationMaster || "",
              DepartmentName:      d.DepartmentName || "",
              DesignationName:     d.DesignationName || "",
              Remark:              d.Remark || "",
              // ESI
              ESIApplicable:       !!d.ESIApplicable,
              ESINo:               d.ESINo               || "",
              ESIJoiningDate:      initialESIDate,
              ESIOldEmployee:      !!d.ESIOldEmployee,
              TICs:                !!d.TICs,
              PICs:                !!d.PICs,
              Accident:            !!d.Accident,
              // PF
              PFApplicable:        !!d.PFApplicable,
              EmplrPFOverLimit:    !!d.EmplrPFOverLimit,
              EmpleeDedOverLimit:  !!d.EmpleeDedOverLimit,
              PensionApplicable:   !!d.PensionApplicable,
              PensionOverLimit:    !!d.PensionOverLimit,
              HigherPension:       !!d.HigherPension,
              PFNo:                d.PFNo                || "",
              UAN:                 d.UAN                 || "",
              PFJoinDate:          initialPFDate,
              ExtraPF:             d.ExtraPF             || "",
              PFCalculationType:   d.PFCalculationType   || "Percentage",
              OldMemberPF:         !!d.OldMemberPF,
              OldMemberPension:    !!d.OldMemberPension,
              PrevPFNo:            d.PrevPFNo            || "",
              OldUAN:              d.OldUAN              || "",
              DateOfExitPr:        d.DateOfExitPr        ? d.DateOfExitPr.substring(0, 10)        : "",
              PMRPY:               !!d.PMRPY,
              PMGKY:               !!d.PMGKY,
              ABRY:                !!d.ABRY,
              FixedPFSalary:       d.FixedPFSalary       || "",
              // Other
              WeeklyHoliday:       d.WeeklyHoliday       || "Sunday",
              BankName:            d.BankName            || "",
              BankBranch:          d.BankBranch          || "",
              IFSCCode:            d.IFSCCode            || "",
              BankAccountNo:       d.BankAccountNo       || "",
              NameAsBank:          d.NameAsBank          || "",
              PanCardNo:           d.PanCardNo           || "",
              NameAsPerPan:        d.NameAsPerPan        || "",
              DrivingLicenceNo:    d.DrivingLicenceNo    || "",
              DLExpiryDate:        d.DLExpiryDate        ? d.DLExpiryDate.substring(0, 10)        : "",
              NameAsPerDL:         d.NameAsPerDL         || "",
              PassportNo:          d.PassportNo          || "",
              PassportFrom:        d.PassportFrom        ? d.PassportFrom.substring(0, 10)        : "",
              PassportTill:        d.PassportTill        ? d.PassportTill.substring(0, 10)        : "",
              PassportName:        d.PassportName        || "",
              IsInternationalEmployee: !!d.IsInternationalEmployee,
              CountryOrigin:       d.CountryOrigin       || "",
              Nationality:         d.Nationality         || "",
              F_CountryMaster:     d.F_CountryMaster     || 1,
              // KYC
              VoterIDNo:           d.VoterIDNo           || "",
              NameAsPerVoterID:    d.NameAsPerVoterID    || "",
              RationCardNo:        d.RationCardNo        || d.RasanCardNo || "",
              NameAsPerRationCard: d.NameAsPerRationCard || d.NameAsPerRasanCard || "",
              NationalPopulationNo:d.NationalPopulationNo|| "",
              NameAsPerNPR:        d.NameAsPerNPR        || "",
              KYCEmployeeType:     d.KYCEmployeeType     || "",
              OpeningBalanceSalary:d.OpeningBalanceSalary|| "",
              // Pension Nominee / Op.Bal
              EmployeePartOpBal:   d.EmployeePartOpBal   || "",
              EmployerPartOpBal:   d.EmployerPartOpBal   || "",
              OpBalYear:           d.OpBalYear           || d.Year || "",
              Year:                d.Year                || d.OpBalYear || "",
              Photo:               loadedPhoto,
              PhotoFile:           null,
              PhotoPreview:        "",
              Signature:           loadedSignature,
              SignatureFile:       null,
              SignaturePreview:    "",
              photoSignatureJson:  rawPhotoSigJson,
              DepartmentJson:      d.DepartmentJson || "",
              ShiftJson:           d.ShiftJson || "",
              JoinJson:            d.JoinJson || "",
              SalaryJson:          d.SalaryJson || "",
              NomineeJson:         d.NomineeJson || "",
              RelativeJson:        d.RelativeJson || "",
              PensionNomineeJson:  d.PensionNomineeJson || "",
              MinWagesJson:        d.MinWagesJson || "",
              ExtraPFRateJson:     d.ExtraPFRateJson || "",
              AllowanceJson:       d.AllowanceJson || "",
              DeductionJson:       d.DeductionJson || "",
            },
          }));
        }
      } else {
        if (firmId) {
          checkMaxWorkers(firmId, 0);
        }
        setState((prev) => ({
          ...prev,
          formData: {
            ...prev.formData,
            F_FirmMaster: firmId ? String(firmId) : "",
            F_BranchMaster: branchId ? String(branchId) : "",
          }
        }));
      }
    };
    fetchData();
  }, [dispatch, location.state, firmId, branchId]);

  // Load branches when firm changes
  const handleFirmChange = (selectedFId, handleChange, e) => {
    handleChange(e);
    if (selectedFId) {
      Fn_FillListData(dispatch, setState, "BranchMaster", `${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${selectedFId}`);
      checkMaxWorkers(selectedFId, state.id);
    } else {
      setState((prev) => ({ ...prev, BranchMaster: [] }));
      setMaxWorkers(null);
      setExistingCount(0);
    }
  };

  // Generic row helpers
  const addRow    = (setter, empty) => setter((p) => [...p, empty()]);
  const removeRow = (setter, index) => setter((p) => p.length === 1 ? p : p.filter((_, i) => i !== index));
  const changeRow = (setter, index, field, value) =>
    setter((p) => p.map((r, i) => i === index ? { ...r, [field]: value } : r));

  const changeDeptRow = (index, field, value, setFieldValue, values) => {
    setDeptRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

    if (field === "EffectiveDate") {
      const cleanDate = value ? value.split("T")[0] : "";
      setSalaryRows((prev) =>
        prev.map((sRow, i) => {
          if (i === index || (index === 0 && (!sRow.EffectiveDate || sRow.EffectiveDate === ""))) {
            return { ...sRow, EffectiveDate: cleanDate };
          }
          return sRow;
        })
      );

      if (setFieldValue && index === 0) {
        setFieldValue("ESIJoiningDate", cleanDate);
        setFieldValue("PFJoinDate", cleanDate);
      }
    }
  };

  const changeSalaryRow = (index, field, value) => {
    setSalaryRows((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const updated = { ...r, [field]: value };
        if (field === "BasicSalary") {
          const salVal = parseFloat(value) || 0;
          if (salVal > 0 && salVal <= 2500) {
            const perDayType = state.BasicTypeMaster?.find(b => b.Id === 2 || String(b.Name).toLowerCase().includes("per day") || String(b.Name).toLowerCase().includes("daily"));
            if (perDayType) {
              updated.F_BasicTypeMaster = perDayType.Id;
            } else {
              updated.F_BasicTypeMaster = 2;
            }
          } else if (salVal > 2500) {
            const monthlyType = state.BasicTypeMaster?.find(b => b.Id === 1 || String(b.Name).toLowerCase().includes("month"));
            if (monthlyType) {
              updated.F_BasicTypeMaster = monthlyType.Id;
            } else {
              updated.F_BasicTypeMaster = 1;
            }
          }
        }
        return updated;
      })
    );
  };
  
  const changeAllowanceCalculation = (index, calculation) => {
    setAllowanceRows(prev => prev.map((row, i) => {
      if (i !== index) return row;
      const updated = { ...row, Calculation: calculation };
      if (calculation === "DAILY" || calculation === "PERCENTAGE") {
        updated.IsWorkingDaysApply = false;
      }
      return updated;
    }));
  };

  const changeDeductionCalculation = (index, calculation) => {
    setDeductionRows(prev => prev.map((row, i) => {
      if (i !== index) return row;
      const updated = { ...row, Calculation: calculation };
      if (calculation === "DAILY" || calculation === "PERCENTAGE") {
        updated.IsWorkingDaysApply = false;
      }
      return updated;
    }));
  };

  const ActionCell = ({ index, rows, onAdd, onRemove }) => (
    <td className="text-center align-middle" style={{ whiteSpace: "nowrap" }}>
      <button type="button" className="btn btn-success btn-xs me-1" onClick={onAdd} title="Add"><i className="fa fa-plus" /></button>
      <button type="button" className="btn btn-danger btn-xs" onClick={() => onRemove(index)} disabled={rows.length === 1} title="Remove"><i className="fa fa-minus" /></button>
    </td>
  );

  const handleSubmit = async (values) => {
    try {
      if (state.id === 0 && maxWorkers !== null && maxWorkers > 0) {
        if (existingCount >= maxWorkers) {
          alert(`Cannot create employee. Firm's limit of ${maxWorkers} workers has been reached.`);
          toast.error(`Cannot create employee. Firm's limit of ${maxWorkers} workers has been reached.`);
          return;
        }
      }
      const formData = new FormData();
      const activeFirmId = values.F_FirmMaster || firmId || 0;
      const activeBranchId = values.F_BranchMaster || branchId || 0;

      const numOrZero = (v) => (v !== null && v !== undefined && v !== "" && v !== "none" && !isNaN(Number(v)) ? Number(v) : 0);
      const strVal = (v) => (v !== null && v !== undefined && String(v).trim() !== "" && v !== "none" && v !== "0" ? String(v) : "");
      const dateVal = (v) => (v !== null && v !== undefined && String(v).trim() !== "" && v !== "0" && v !== "none" ? String(v) : "");
      const boolVal = (v) => (v ? true : false);

      const empName = strVal(values.Name);
      formData.append("F_FirmMaster",        numOrZero(activeFirmId));
      formData.append("F_BranchMaster",      numOrZero(activeBranchId));
      formData.append("SerialNo",            state.id > 0 ? numOrZero(values.SerialNo) : 0);
      formData.append("Name",                empName);
      formData.append("FatherName",          strVal(values.FatherName));
      formData.append("EmployeeType",        strVal(values.EmployeeType || "Ordinary"));
      formData.append("CardNo",              strVal(values.CardNo));
      formData.append("Sex",                 strVal(values.Sex || "M"));
      formData.append("DateOfBirth",         dateVal(values.DateOfBirth));
      formData.append("DateOfRetire",        dateVal(values.DateOfRetire));
      formData.append("IsDeferredPension",    boolVal(values.IsDeferredPension));
      formData.append("PensionAge",           numOrZero(values.PensionAge));
      formData.append("BankPayment",          boolVal(values.BankPayment));
      formData.append("F_ShiftMaster",        numOrZero(values.F_ShiftMaster));
      formData.append("PermanentAddress",    strVal(values.PermanentAddress));
      formData.append("PresentAddress",      strVal(values.PresentAddress));
      formData.append("MobileNo",            strVal(values.MobileNo));
      formData.append("Email",               strVal(values.Email));
      formData.append("EdQualification",      strVal(values.EdQualification || values.EducationQualification));
      formData.append("EducationQualification", strVal(values.EducationQualification || values.EdQualification));
      formData.append("IsHandicap",          boolVal(values.IsHandicap));
      formData.append("HandicapCategory",    strVal(values.HandicapCategory));
      formData.append("Status",              strVal(values.Status || "Working"));
      formData.append("AadharNumber",        strVal(values.AadharNumber || values.AadharNo));
      formData.append("AadharNo",            strVal(values.AadharNo || values.AadharNumber));
      formData.append("NameInAadhar",        strVal(values.NameInAadhar) || empName);
      formData.append("MaritalStatus",       boolVal(values.MaritalStatus));
      formData.append("Married",             strVal(values.Married || "Unmarried"));
      formData.append("RefundFileDate",      dateVal(values.RefundFileDate));
      formData.append("Refunded",            boolVal(values.Refunded));
      formData.append("GratuityPaidDate",    dateVal(values.GratuityPaidDate));

      const latestDept = deptRows?.[deptRows.length - 1] || deptRows?.[0];
      const deptId = latestDept?.F_DepartmentMaster || values.F_DepartmentMaster;
      const desigId = latestDept?.F_DesignationMaster || values.F_DesignationMaster;
      formData.append("F_DepartmentMaster",  numOrZero(deptId));
      formData.append("F_DesignationMaster", numOrZero(desigId));
      formData.append("Remark",              strVal(values.Remark));
      
      // ESI
      formData.append("ESIApplicable",       boolVal(values.ESIApplicable));
      formData.append("ESINo",               strVal(values.ESINo));
      formData.append("ESIJoiningDate",      dateVal(values.ESIJoiningDate));
      formData.append("ESIOldEmployee",      boolVal(values.ESIOldEmployee));
      formData.append("TICs",                boolVal(values.TICs));
      formData.append("PICs",                boolVal(values.PICs));
      formData.append("Accident",            boolVal(values.Accident));
      
      // PF
      formData.append("PFApplicable",        boolVal(values.PFApplicable));
      formData.append("EmplrPFOverLimit",    boolVal(values.EmplrPFOverLimit));
      formData.append("EmpleeDedOverLimit",  boolVal(values.EmpleeDedOverLimit));
      formData.append("PensionApplicable",   boolVal(values.PensionApplicable));
      formData.append("PensionOverLimit",    boolVal(values.PensionOverLimit));
      formData.append("HigherPension",       boolVal(values.HigherPension));
      formData.append("PFNo",                strVal(values.PFNo));
      formData.append("UAN",                 strVal(values.UAN));
      formData.append("PFJoinDate",          dateVal(values.PFJoinDate));
      formData.append("ExtraPF",             numOrZero(values.ExtraPF));
      formData.append("PFCalculationType",   strVal(values.PFCalculationType || "Percentage"));
      formData.append("OldMemberPF",         boolVal(values.OldMemberPF));
      formData.append("OldMemberPension",    boolVal(values.OldMemberPension));
      formData.append("PrevPFNo",            strVal(values.PrevPFNo));
      formData.append("OldUAN",              strVal(values.OldUAN));
      formData.append("DateOfExitPr",        dateVal(values.DateOfExitPr));
      formData.append("PMRPY",               boolVal(values.PMRPY));
      formData.append("PMGKY",               boolVal(values.PMGKY));
      formData.append("ABRY",                boolVal(values.ABRY));
      formData.append("FixedPFSalary",       numOrZero(values.FixedPFSalary));
      
      // Other
      formData.append("WeeklyHoliday",       strVal(values.WeeklyHoliday || "Sunday"));
      formData.append("BankName",            strVal(values.BankName));
      formData.append("BankBranch",          strVal(values.BankBranch));
      formData.append("IFSCCode",            strVal(values.IFSCCode));
      formData.append("BankAccountNo",       strVal(values.BankAccountNo));
      formData.append("NameAsBank",          strVal(values.NameAsBank) || empName);
      formData.append("PanCardNo",           strVal(values.PanCardNo));
      formData.append("NameAsPerPan",        strVal(values.NameAsPerPan) || empName);
      formData.append("DrivingLicenceNo",    strVal(values.DrivingLicenceNo));
      formData.append("DLExpiryDate",        dateVal(values.DLExpiryDate));
      formData.append("NameAsPerDL",         strVal(values.NameAsPerDL) || empName);
      formData.append("PassportNo",          strVal(values.PassportNo));
      formData.append("PassportFrom",        dateVal(values.PassportFrom));
      formData.append("PassportTill",        dateVal(values.PassportTill));
      formData.append("PassportName",        strVal(values.PassportName) || empName);
      formData.append("IsInternationalEmployee", boolVal(values.IsInternationalEmployee));
      formData.append("CountryOrigin",       strVal(values.CountryOrigin));
      formData.append("Nationality",         strVal(values.Nationality));
      formData.append("F_CountryMaster",     numOrZero(values.F_CountryMaster));
      
      // KYC Fields
      formData.append("VoterIDNo",           strVal(values.VoterIDNo));
      formData.append("NameAsPerVoterID",    strVal(values.NameAsPerVoterID) || empName);
      formData.append("RationCardNo",        strVal(values.RationCardNo));
      formData.append("NameAsPerRationCard", strVal(values.NameAsPerRationCard) || empName);
      formData.append("NationalPopulationNo",strVal(values.NationalPopulationNo));
      formData.append("NameAsPerNPR",        strVal(values.NameAsPerNPR) || empName);
      formData.append("KYCEmployeeType",     numOrZero(values.KYCEmployeeType || values.F_KYCEmployeeTypeMaster));
      formData.append("F_KYCEmployeeTypeMaster", numOrZero(values.F_KYCEmployeeTypeMaster || values.KYCEmployeeType));
      formData.append("OpeningBalanceSalary",numOrZero(values.OpeningBalanceSalary));

      // Pension Nominee & Op.Bal
      formData.append("EmployeePartOpBal",   numOrZero(values.EmployeePartOpBal));
      formData.append("EmployerPartOpBal",   numOrZero(values.EmployerPartOpBal));
      formData.append("OpBalYear",           numOrZero(values.OpBalYear || values.Year));
      formData.append("Year",                numOrZero(values.Year || values.OpBalYear));
      
      const base64ToBlob = (base64Data, defaultType = 'image/jpeg') => {
        try {
          const byteCharacters = atob(base64Data);
          const byteArrays = [];
          for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
              byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
          }
          return new Blob(byteArrays, { type: defaultType });
        } catch (e) {
          return null;
        }
      };

      const toBinaryFile = (imgInput, defaultFileName = "image.jpg") => {
        if (!imgInput) return null;
        if (imgInput instanceof File || imgInput instanceof Blob) {
          return imgInput;
        }
        if (typeof imgInput === "string" && imgInput.startsWith("data:image")) {
          const parts = imgInput.split(",");
          const mimeMatch = parts[0].match(/:(.*?);/);
          const mimeType = mimeMatch ? mimeMatch[1] : "image/jpeg";
          const base64 = parts[1];
          const blob = base64ToBlob(base64, mimeType);
          if (blob) {
            const ext = mimeType.split("/")[1] || "jpg";
            return new File([blob], defaultFileName || `image.${ext}`, { type: mimeType });
          }
        }
        return null;
      };

      // Append Photo and Signature files in binary format
      const photoBin = toBinaryFile(values.PhotoFile || values.Photo, "employee_photo.jpg");
      if (photoBin) {
        formData.append("Photo", photoBin, "employee_photo.jpg");
        formData.append("EmployeePhoto", photoBin, "employee_photo.jpg");
      } else {
        formData.append("Photo", strVal(values.Photo));
        formData.append("EmployeePhoto", strVal(values.Photo));
      }

      const sigBin = toBinaryFile(values.SignatureFile || values.Signature, "employee_signature.jpg");
      if (sigBin) {
        formData.append("Signature", sigBin, "employee_signature.jpg");
      } else {
        formData.append("Signature", strVal(values.Signature));
      }
      
      // Sanitize JSON rows and filter empty rows
      const cleanDeptRows = deptRows.map(r => ({
        ...r,
        F_DepartmentMaster: r.F_DepartmentMaster && r.F_DepartmentMaster !== "none" ? Number(r.F_DepartmentMaster) : 0,
        F_DesignationMaster: r.F_DesignationMaster && r.F_DesignationMaster !== "none" ? Number(r.F_DesignationMaster) : 0
      })).filter(r => r.F_DepartmentMaster > 0 || r.F_DesignationMaster > 0);

      const cleanShiftRows = shiftRows.map(r => ({
        ...r,
        F_ShiftMaster: r.F_ShiftMaster && r.F_ShiftMaster !== "none" ? Number(r.F_ShiftMaster) : 0
      })).filter(r => r.F_ShiftMaster > 0);

      const cleanJoinRows = joinRows.map(r => ({
        ...r,
        F_JoinTypeMaster: r.F_JoinTypeMaster && r.F_JoinTypeMaster !== "none" ? Number(r.F_JoinTypeMaster) : 0
      })).filter(r => r.F_JoinTypeMaster > 0 || r.JoinDate);

      const cleanSalaryRows = salaryRows.map(r => ({
        ...r,
        BasicSalary: r.BasicSalary ? Number(r.BasicSalary) : 0,
        F_BasicTypeMaster: r.F_BasicTypeMaster && r.F_BasicTypeMaster !== "none" ? Number(r.F_BasicTypeMaster) : 0
      })).filter(r => r.BasicSalary > 0 || r.F_BasicTypeMaster > 0);

      if (cleanSalaryRows.length === 0) {
        const msg = "Please add at least one Salary details row with Basic Salary and Basic Type.";
        toast.error(msg);
        alert(msg);
        setActiveTab("salary");
        return;
      }

      for (let i = 0; i < cleanSalaryRows.length; i++) {
        const row = cleanSalaryRows[i];
        if (!row.F_BasicTypeMaster) {
          const msg = `Salary Row ${i + 1}: Basic Type is required.`;
          toast.error(msg);
          alert(msg);
          setActiveTab("salary");
          return;
        }
        if (row.BasicSalary <= 0) {
          const msg = `Salary Row ${i + 1}: Basic Salary is required.`;
          toast.error(msg);
          alert(msg);
          setActiveTab("salary");
          return;
        }
      }

      // ── Validation for Allowances: Date & Rate (Amount) required ──
      for (let i = 0; i < allowanceRows.length; i++) {
        const row = allowanceRows[i];
        const masterId = row.F_AllowanceMaster && row.F_AllowanceMaster !== "none" ? Number(row.F_AllowanceMaster) : 0;
        const hasAnyValue = masterId > 0 || (row.Amount && Number(row.Amount) > 0) || (row.EffectiveFrom && String(row.EffectiveFrom).trim() !== "");
        
        if (hasAnyValue) {
          const allowanceName = state.AllowanceMaster?.find(a => String(a.Id) === String(row.F_AllowanceMaster))?.Name || `Allowance #${i + 1}`;
          
          if (!masterId) {
            const msg = `Allowance Row ${i + 1}: Name is required.`;
            toast.error(msg);
            alert(msg);
            setActiveTab("allowances");
            setExpandedAllowances(prev => ({ ...prev, [`new_${i}`]: true }));
            return;
          }
          if (!row.EffectiveFrom || String(row.EffectiveFrom).trim() === "") {
            const msg = `Allowance (${allowanceName}): Date (Effective From) is required.`;
            toast.error(msg);
            alert(msg);
            setActiveTab("allowances");
            setExpandedAllowances(prev => ({ ...prev, [row.F_AllowanceMaster]: true, [`new_${i}`]: true }));
            return;
          }
          if (row.Amount === "" || row.Amount === undefined || row.Amount === null || Number(row.Amount) <= 0) {
            const msg = `Allowance (${allowanceName}): Rate (Amount) is required.`;
            toast.error(msg);
            alert(msg);
            setActiveTab("allowances");
            setExpandedAllowances(prev => ({ ...prev, [row.F_AllowanceMaster]: true, [`new_${i}`]: true }));
            return;
          }
        }
      }

      // ── Validation for Deductions: Date & Rate (Amount) required ──
      for (let i = 0; i < deductionRows.length; i++) {
        const row = deductionRows[i];
        const masterId = row.F_DeductionMaster && row.F_DeductionMaster !== "none" ? Number(row.F_DeductionMaster) : 0;
        const hasAnyValue = masterId > 0 || (row.Amount && Number(row.Amount) > 0) || (row.EffectiveFrom && String(row.EffectiveFrom).trim() !== "");
        
        if (hasAnyValue) {
          const deductionName = state.DeductionMaster?.find(d => String(d.Id) === String(row.F_DeductionMaster))?.Name || `Deduction #${i + 1}`;
          
          if (!masterId) {
            const msg = `Deduction Row ${i + 1}: Name is required.`;
            toast.error(msg);
            alert(msg);
            setActiveTab("deductions");
            setExpandedDeductions(prev => ({ ...prev, [`new_${i}`]: true }));
            return;
          }
          if (!row.EffectiveFrom || String(row.EffectiveFrom).trim() === "") {
            const msg = `Deduction (${deductionName}): Date (Effective From) is required.`;
            toast.error(msg);
            alert(msg);
            setActiveTab("deductions");
            setExpandedDeductions(prev => ({ ...prev, [row.F_DeductionMaster]: true, [`new_${i}`]: true }));
            return;
          }
          if (row.Amount === "" || row.Amount === undefined || row.Amount === null || Number(row.Amount) <= 0) {
            const msg = `Deduction (${deductionName}): Rate (Amount) is required.`;
            toast.error(msg);
            alert(msg);
            setActiveTab("deductions");
            setExpandedDeductions(prev => ({ ...prev, [row.F_DeductionMaster]: true, [`new_${i}`]: true }));
            return;
          }
        }
      }

      const cleanNomineeRows = nomineeRows.map(r => ({
        ...r,
        F_RelationshipMaster: r.F_RelationshipMaster && r.F_RelationshipMaster !== "none" ? Number(r.F_RelationshipMaster) : 0,
        Amount: r.Amount ? Number(r.Amount) : 0,
        Photo: r.Photo || ""
      })).filter(r => r.NomineeName || r.F_RelationshipMaster > 0 || r.Photo);

      const cleanRelativeRows = relativeRows.map(r => ({
        ...r,
        F_RelationshipMaster: r.F_RelationshipMaster && r.F_RelationshipMaster !== "none" ? Number(r.F_RelationshipMaster) : 0,
        LivingWith: r.LivingWith ? Number(r.LivingWith) : 0,
        ForESI: r.ForESI ? Number(r.ForESI) : 0,
        Photo: r.Photo || ""
      })).filter(r => r.RelativeName || r.F_RelationshipMaster > 0 || r.Photo);

      const cleanPensionNomineeRows = pensionNomineeRows.map(r => ({
        ...r,
        F_RelationshipMaster: r.F_RelationshipMaster && r.F_RelationshipMaster !== "none" ? Number(r.F_RelationshipMaster) : 0,
        Amount: r.Amount ? Number(r.Amount) : 0,
        Photo: r.Photo || ""
      })).filter(r => r.NomineeName || r.F_RelationshipMaster > 0 || r.NameHindi || r.Photo);

      const cleanMinWagesRows = minWagesRows.map(r => ({
        ...r,
        MinWages: r.MinWages ? Number(r.MinWages) : 0
      })).filter(r => r.MinWages > 0 || r.EffectiveDate);

      const cleanExtraPFRateRows = extraPFRateRows.map(r => ({
        ...r,
        ExtraPFRate: r.ExtraPFRate ? Number(r.ExtraPFRate) : 0,
        IsManual: r.IsManual ? true : false
      })).filter(r => r.ExtraPFRate > 0 || r.EffectiveDate);

      formData.append("DepartmentJson",     JSON.stringify(cleanDeptRows));
      formData.append("ShiftJson",          JSON.stringify(cleanShiftRows));
      formData.append("JoinJson",           JSON.stringify(cleanJoinRows));
      formData.append("SalaryJson",         JSON.stringify(cleanSalaryRows));
      formData.append("NomineeJson",        JSON.stringify(cleanNomineeRows));
      formData.append("RelativeJson",       JSON.stringify(cleanRelativeRows));
      formData.append("PensionNomineeJson", JSON.stringify(cleanPensionNomineeRows));
      formData.append("MinWagesJson",       JSON.stringify(cleanMinWagesRows));
      formData.append("ExtraPFRateJson",    JSON.stringify(cleanExtraPFRateRows));

      // Helper to preserve history ONLY when EffectiveFrom (Date) changes
      const buildVersionedRows = (currentRows, originalRows, masterKey) => {
        const normalizeDate = (d) => (d ? String(d).split("T")[0].trim() : "");
        
        const cleanRow = (r) => ({
          ...r,
          [masterKey]: Number(r[masterKey]),
          Amount: r.Amount ? Number(r.Amount) : 0,
          Calculation: r.Calculation || "MANUAL",
          IsOnGross: !!r.IsOnGross,
          IsPFApply: !!r.IsPFApply,
          IsESIApply: !!r.IsESIApply,
          IsRoundOffApply: r.IsRoundOffApply !== undefined ? !!r.IsRoundOffApply : true,
          IsWorkingDaysApply: !!r.IsWorkingDaysApply,
          IsApplicable: r.IsApplicable !== undefined ? !!r.IsApplicable : true,
          EffectiveFrom: normalizeDate(r.EffectiveFrom),
        });

        const validCurrent = (currentRows || [])
          .filter(r => r[masterKey] && r[masterKey] !== "none" && Number(r[masterKey]) > 0)
          .map(cleanRow);

        const validOriginal = (originalRows || [])
          .filter(r => r[masterKey] && r[masterKey] !== "none" && Number(r[masterKey]) > 0)
          .map(cleanRow);

        const resultMap = new Map();
        const makeKey = (masterId, effDate) => `${masterId}__${effDate}`;

        const origGroups = {};
        validOriginal.forEach(orig => {
          const mId = orig[masterKey];
          if (!origGroups[mId]) origGroups[mId] = [];
          origGroups[mId].push(orig);
        });

        const currentGroups = {};
        validCurrent.forEach(curr => {
          const mId = curr[masterKey];
          if (!currentGroups[mId]) currentGroups[mId] = [];
          currentGroups[mId].push(curr);
        });

        Object.keys(currentGroups).forEach(mIdStr => {
          const mId = Number(mIdStr);
          const currList = currentGroups[mId];
          const origList = origGroups[mId] || [];

          // Preserve old version in history ONLY if its EffectiveFrom date is NOT in current rows (i.e. date was changed to a new date)
          origList.forEach(orig => {
            const origDate = orig.EffectiveFrom;
            const matchingCurr = currList.find(c => c.EffectiveFrom === origDate);
            if (!matchingCurr && origDate) {
              resultMap.set(makeKey(mId, origDate), orig);
            }
          });

          // Add current rows (with either existing unchanged date or new changed date)
          currList.forEach(curr => {
            resultMap.set(makeKey(mId, curr.EffectiveFrom), curr);
          });
        });

        return Array.from(resultMap.values());
      };

      const finalAllowance = buildVersionedRows(allowanceRows, originalAllowanceRows, "F_AllowanceMaster");
      formData.append("AllowanceJson", JSON.stringify(finalAllowance));

      const finalDeduction = buildVersionedRows(deductionRows, originalDeductionRows, "F_DeductionMaster");
      formData.append("DeductionJson", JSON.stringify(finalDeduction));

      const photoVal = values.PhotoPreview || strVal(values.Photo);
      const sigVal = values.SignaturePreview || strVal(values.Signature);
      const photoSignatureData = [
        {
          Photo: photoVal,
          Signature: sigVal
        }
      ];
      const photoSignatureJsonStr = JSON.stringify(photoSignatureData);

      formData.append("photoSignatureJson", photoSignatureJsonStr);
      formData.append("PhotoSignatureJson", photoSignatureJsonStr);

      Fn_AddEditData(
        dispatch, setState,
        { arguList: { id: state.id, formData } },
        API_URL_SAVE, true, "memberid", navigate, "/EmployeeMaster"
      );
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const handleTabKeyDown = (e) => {
    if (e.key === "Enter") {
      const container = document.querySelector(".tab-pane-content");
      if (!container) return;
      
      const inputs = Array.from(container.querySelectorAll("input:not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled])"));
      if (inputs.length === 0) return;
      
      const lastInput = inputs[inputs.length - 1];
      if (document.activeElement === lastInput) {
        e.preventDefault();
        if (activeTab === tabOrder[tabOrder.length - 1].key) {
          document.getElementById("btnSubmitForm")?.focus();
        } else {
          document.getElementById("btnNextTab")?.focus();
        }
      }
    }
  };

  const standardTabs = [
    { key: "basicInfo",      label: "EMPLOYEE" },
    { key: "salary",         label: "BASIC / SALARY" },
    { key: "kyc",            label: "KYC" },
    { key: "pensionNominee", label: "PENSION NOMINEE" },
    { key: "department",     label: "Dept. History" },
    { key: "shift",          label: "Shift History" },
    { key: "join",           label: "Join History" },
    { key: "allowances",     label: "Allowances" },
    { key: "deductions",     label: "Deductions" },
  ];

  const [tabOrder, setTabOrder] = useState(() => {
    const saved = localStorage.getItem("employeeTabSequence");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const merged = [];
          parsed.forEach(p => {
            const st = standardTabs.find(s => s.key === p.key);
            if (st) merged.push(st);
          });
          standardTabs.forEach(st => {
            if (!merged.some(m => m.key === st.key)) merged.push(st);
          });
          return merged;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return standardTabs;
  });

  useEffect(() => {
    const fetchTabSequence = async () => {
      try {
        const res = await Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/TabSequencesMaster/Id/0`);
        if (res && Array.isArray(res)) {
          const employeeTabs = res.filter(t => t.ModuleName === "EmployeeMaster");
          if (employeeTabs.length > 0) {
            employeeTabs.sort((a, b) => (a.TabId || 0) - (b.TabId || 0));
            const newOrder = [];
            employeeTabs.forEach(item => {
              const standardTab = standardTabs.find(st => st.key === item.TabName);
              if (standardTab) {
                newOrder.push(standardTab);
              }
            });
            standardTabs.forEach(st => {
              if (!newOrder.some(t => t.key === st.key)) {
                newOrder.push(st);
              }
            });

            if (newOrder.length === standardTabs.length) {
              setTabOrder(newOrder);
              localStorage.setItem("employeeTabSequence", JSON.stringify(newOrder));
            }
          }
        }
      } catch (err) {
        console.error("Error fetching TabSequence from DB:", err);
      }
    };
    fetchTabSequence();
  }, [dispatch]);

  const saveTabSequence = (newOrder) => {
    localStorage.setItem("employeeTabSequence", JSON.stringify(newOrder));
    
    const payload = newOrder.map((t, index) => ({
      TabId: index + 1,
      TabName: t.key,
      IsActive: true
    }));

    const fd = new FormData();
    fd.append("ModuleName", "EmployeeMaster");
    fd.append("TabSequencesJson", JSON.stringify(payload));
    
    Fn_AddEditData(
      dispatch,
      () => {},
      { arguList: { id: 0, formData: fd } },
      "TabSequencesMaster/0/token",
      true,
      "memberid",
      () => {},
      "#"
    ).catch(err => console.error("Error saving TabSequence to DB:", err));
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex) => {
    const sourceIndex = Number(e.dataTransfer.getData("text/plain"));
    if (sourceIndex === targetIndex) return;

    const newOrder = [...tabOrder];
    const [draggedItem] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);
    
    setTabOrder(newOrder);
    saveTabSequence(newOrder);
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Masters" motherMenu="Masters" pageContent="Employee Master" />

      <style>{`
        /* Flexible Container Space */
        .emp-main-wrapper {
          height: calc(100vh - 110px);
          max-height: calc(100vh - 110px);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .emp-compact-form {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          height: 100%;
        }
        .emp-compact-form .card {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          margin-bottom: 0px !important;
          border-radius: 8px;
        }
        .emp-compact-form .card-header {
          padding: 8px 16px !important;
          min-height: unset !important;
          flex-shrink: 0;
          border-bottom: 1px solid #ebedf2;
        }
        .emp-compact-form .card-title {
          font-size: 15px !important;
          font-weight: 600;
          margin-bottom: 0 !important;
        }
        .emp-compact-form .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          padding: 10px 16px !important;
        }
        .emp-compact-form form {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .emp-tab-container {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
          padding-right: 4px;
        }

        /* Tabs Nav */
        .emp-compact-form .nav-tabs {
          margin-bottom: 10px !important;
          border-bottom: 1px solid #dee2e6 !important;
          flex-shrink: 0;
        }
        .emp-compact-form .nav-tabs .nav-link {
          padding: 5px 12px !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          line-height: 1.4 !important;
          border-radius: 4px 4px 0 0;
        }

        /* Responsive CSS Grid & Stacked Groups */
        .emp-responsive-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 10px 14px;
        }
        .emp-form-group {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .emp-form-group-inline {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 8px;
          margin-top: auto;
          padding-bottom: 4px;
        }

        /* Input Box Sizing & Typography */
        .emp-compact-form input:not([type="checkbox"]):not([type="radio"]),
        .emp-compact-form select,
        .emp-compact-form textarea {
          box-sizing: border-box;
        }
        .emp-form-group input,
        .emp-form-group select,
        .emp-form-group textarea {
          width: 100%;
        }
        .emp-compact-form .form-control,
        .emp-compact-form .form-control-sm,
        .emp-compact-form select.form-control-sm {
          height: 28px !important;
          min-height: 28px !important;
          padding: 2px 6px !important;
          font-size: 11.5px !important;
          border-radius: 4px !important;
          border: 1px solid #ced4da;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        }
        .emp-compact-form .form-control:focus,
        .emp-compact-form .form-control-sm:focus {
          border-color: #80bdff;
          outline: 0;
          box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.15);
        }

        /* Standard Field Labels (block) */
        .emp-compact-form label:not(.form-check-label) {
          font-size: 11.5px !important;
          margin-bottom: 2px !important;
          color: #374151 !important;
          font-weight: 600 !important;
          display: block;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        /* Checkbox / Radio Flexbox Alignment & Fixed Dimensions */
        .emp-compact-form .form-check {
          display: inline-flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 6px !important;
          margin-bottom: 0 !important;
          min-height: 28px !important;
          height: 28px !important;
          padding-left: 0 !important;
          cursor: pointer !important;
          white-space: nowrap !important;
        }
        .emp-compact-form .form-check-label {
          font-size: 11.5px !important;
          margin-bottom: 0 !important;
          color: #374151 !important;
          font-weight: 600 !important;
          cursor: pointer !important;
          display: inline-block !important;
          white-space: nowrap !important;
          overflow: visible !important;
          text-overflow: unset !important;
        }
        .emp-compact-form input[type="checkbox"],
        .emp-compact-form input[type="radio"],
        .emp-compact-form .form-check-input {
          box-sizing: border-box !important;
          width: 16px !important;
          height: 16px !important;
          min-width: 16px !important;
          min-height: 16px !important;
          max-width: 16px !important;
          max-height: 16px !important;
          margin: 0 !important;
          padding: 0 !important;
          cursor: pointer !important;
          flex-shrink: 0 !important;
          display: inline-block !important;
          vertical-align: middle !important;
          appearance: auto !important;
          -webkit-appearance: checkbox !important;
        }

        /* Tables & Action Cells */
        .emp-compact-form table.table-sm th,
        .emp-compact-form table.table-sm td {
          padding: 4px 6px !important;
          font-size: 11.5px !important;
          vertical-align: middle !important;
        }
        .emp-compact-form .btn-xs {
          padding: 2px 6px !important;
          font-size: 11px !important;
          line-height: 1.2 !important;
        }

        /* Anchored Action Buttons */
        .emp-footer-actions {
          margin-top: auto;
          padding-top: 10px;
          border-top: 1px solid #dee2e6;
          display: flex;
          align-items: center;
          gap: 10px;
          background: #fff;
          flex-shrink: 0;
        }
      `}</style>

      <div className="row emp-main-wrapper emp-compact-form">
        <div className="col-lg-12 h-100 d-flex flex-column">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Employee Master</h4>
            </div>
            <div className="card-body">

              {/* Tabs Nav */}
              <ul className="nav nav-tabs mb-2 flex-wrap" role="tablist">
                {tabOrder.map((tab, idx) => (
                  <li className="nav-item" key={tab.key}>
                    <button
                      className={`nav-link${activeTab === tab.key ? " active" : ""}`}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, idx)}
                      style={{ cursor: "grab" }}
                    >
                      <span className="me-1 text-muted" style={{ userSelect: "none" }}>⋮⋮</span>
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>

              <Formik
                initialValues={state.formData}
                enableReinitialize
                validationSchema={EmployeeSchema}
                onSubmit={(values, { setSubmitting }) => {
                  setTimeout(() => { handleSubmit(values); setSubmitting(false); }, 400);
                }}
              >
                {({ values, errors, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => {
                  const currentAge = calculateAge(values.DateOfBirth);
                  const availableShifts = getBranchShifts(values.F_BranchMaster, values.F_FirmMaster);

                  return (
                    <form onSubmit={handleSubmit} onKeyDown={handleTabKeyDown}>

                      {/* Form Container Flexibility: Flex child taking remaining space */}
                      <div className="emp-tab-container">
                        {activeTab !== "basicInfo" && (
                          <div className="alert alert-info py-1 px-2 mb-2 d-flex align-items-center justify-content-between flex-wrap gap-1" style={{ borderRadius: 4, fontSize: "11px", flexShrink: 0 }}>
                            <div>
                              <strong className="text-dark me-1">Emp Name:</strong>
                              <span className="text-primary fw-semibold me-3">{values.Name || "N/A"}</span>
                              
                              <strong className="text-dark me-1">Father:</strong>
                              <span className="text-muted me-3">{values.FatherName || "N/A"}</span>

                              <strong className="text-dark me-1">Emp Code:</strong>
                              <span className="text-danger fw-semibold">{values.CardNo || "N/A"}</span>
                            </div>
                            <div className="badge bg-light text-primary border px-2 py-0" style={{ fontSize: 10.5 }}>
                              {tabOrder.find(t => t.key === activeTab)?.label}
                            </div>
                          </div>
                        )}

                        {/* ════════════════════════════════════════════════════════════════════════
                            TAB 1: EMPLOYEE (Basic Info) - Compact High-Density Grid
                            ════════════════════════════════════════════════════════════════════════ */}
                        {activeTab === "basicInfo" && (
                          <div className="tab-pane-content emp-compact-tab">
                          
                          {/* Row 1: Serial No (Edit only, permanently disabled) | ESI Firm | Branch | Emp. Name | Employee Type */}
                          <div className="row gx-2 gy-1 mb-1 align-items-end">
                            {state.id > 0 && (
                              <div className="col-6 col-md-2 col-xl-1">
                                <label className="text-label fw-bold">Serial No:</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="SerialNo"
                                  value={values.SerialNo || ""}
                                  readOnly
                                  disabled
                                  style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed", fontWeight: 700 }}
                                  title={`Firm-wise Serial Number: ${values.SerialNo}`}
                                />
                              </div>
                            )}
                            <div className="col-12 col-md-4 col-xl-3">
                              <label className="text-label fw-bold">
                                ESI Firm : * {firmId ? <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "9px", padding: "1px 4px" }}>🔒 Locked</span> : null}
                              </label>
                              <select
                                className="form-control form-control-sm"
                                name="F_FirmMaster"
                                value={values.F_FirmMaster || firmId}
                                disabled={!!firmId}
                                style={firmId ? { backgroundColor: "#f8f9fa", cursor: "not-allowed" } : {}}
                                onBlur={handleBlur}
                                onChange={(e) => handleFirmChange(e.target.value, handleChange, e)}
                              >
                                <option value="">-- Select Firm --</option>
                                {state.FirmMaster.map((f) => <option key={f.Id} value={f.Id}>{f.Name}</option>)}
                              </select>
                              {errors.F_FirmMaster && <div className="text-danger fs-11">{errors.F_FirmMaster}</div>}
                            </div>
                            <div className={state.id > 0 ? "col-12 col-md-3 col-xl-2" : "col-12 col-md-3 col-xl-3"}>
                              <label className="text-label fw-bold">Branch:</label>
                              <select
                                className="form-control form-control-sm"
                                name="F_BranchMaster"
                                value={values.F_BranchMaster || ""}
                                onChange={(e) => {
                                  handleChange(e);
                                  const newBranchId = e.target.value;
                                  const shiftsForNewBranch = getBranchShifts(newBranchId, values.F_FirmMaster);
                                  if (values.F_ShiftMaster && !shiftsForNewBranch.some(s => String(s.Id) === String(values.F_ShiftMaster))) {
                                    setFieldValue("F_ShiftMaster", "");
                                  }
                                }}
                                onBlur={handleBlur}
                              >
                                <option value="">-- Select Branch --</option>
                                {state.BranchMaster.map((b) => <option key={b.Id} value={b.Id}>{b.Name}</option>)}
                              </select>
                            </div>
                            <div className="col-12 col-md-6 col-xl-3">
                              <label className="text-label fw-bold">Emp. Name: *</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                name="Name"
                                value={values.Name || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const prevName = values.Name || "";
                                  setFieldValue("Name", val);

                                  const docFields = [
                                    "NameInAadhar",
                                    "NameAsBank",
                                    "NameAsPerPan",
                                    "NameAsPerDL",
                                    "PassportName",
                                    "NameAsPerVoterID",
                                    "NameAsPerRationCard",
                                    "NameAsPerNPR",
                                  ];

                                  docFields.forEach((f) => {
                                    if (!values[f] || values[f] === prevName) {
                                      setFieldValue(f, val);
                                    }
                                  });
                                }}
                                onBlur={handleBlur}
                                placeholder="Employee Full Name"
                              />
                              {errors.Name && <div className="text-danger fs-11">{errors.Name}</div>}
                            </div>
                            <div className="col-12 col-md-5 col-xl-3">
                              <label className="text-label fw-bold">Employee Type :</label>
                              <select
                                className="form-control form-control-sm"
                                name="EmployeeType"
                                value={values.EmployeeType || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">-- Select Employee Type --</option>
                                {((state.EmployeeTypeMaster && state.EmployeeTypeMaster.length > 0)
                                  ? state.EmployeeTypeMaster
                                  : [
                                      { Id: 1, Name: "Ordinary" },
                                      { Id: 2, Name: "Special" },
                                      { Id: 3, Name: "Permanent" },
                                      { Id: 4, Name: "Temporary" },
                                      { Id: 5, Name: "Contract" },
                                      { Id: 6, Name: "Daily Wage" }
                                    ]
                                ).map((t) => (
                                  <option key={t.Id || t.Name} value={t.Name || t.Id}>
                                    {t.Name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Row 2: Father Name | Card No | Sex | Deferred Pension | DOB & Age | Date of Retire */}
                          <div className="row gx-2 gy-1 mb-1 align-items-end">
                            <div className="col-12 col-md-4 col-xl-3">
                              <label className="text-label fw-bold">Father Name:</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                name="FatherName"
                                value={values.FatherName || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Father Name"
                              />
                            </div>
                            <div className="col-6 col-md-3 col-xl-2">
                              <label className="text-label fw-bold">Employee Code:</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                name="CardNo"
                                value={values.CardNo || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isAutoEmpCode}
                                placeholder={isAutoEmpCode ? "Auto generated after save" : "Employee Code"}
                              />
                            </div>
                            <div className="col-6 col-md-2 col-xl-1">
                              <label className="text-label fw-bold">Sex:</label>
                              <select className="form-control form-control-sm" name="Sex" value={values.Sex || "M"} onChange={handleChange} onBlur={handleBlur}>
                                <option value="M">M</option>
                                <option value="F">F</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                            <div className="col-12 col-md-3 col-xl-2">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="IsDeferredPension"
                                  name="IsDeferredPension"
                                  checked={!!values.IsDeferredPension}
                                  onChange={(e) => {
                                    const val = e.target.checked;
                                    setFieldValue("IsDeferredPension", val);
                                    if (!val) {
                                      setFieldValue("PensionAge", "");
                                      if (values.DateOfBirth && retirementAge) {
                                        setFieldValue("DateOfRetire", calculateRetirementDate(values.DateOfBirth, retirementAge));
                                      }
                                    } else {
                                      if (values.DateOfBirth && values.PensionAge) {
                                        setFieldValue("DateOfRetire", calculateRetirementDate(values.DateOfBirth, values.PensionAge));
                                      }
                                    }
                                  }}
                                />
                                <label htmlFor="IsDeferredPension" className="form-check-label">Deferred Pension:</label>
                                {values.IsDeferredPension && (
                                  <input
                                    type="number"
                                    className="form-control form-control-sm ms-1 px-1 text-center"
                                    style={{ width: "45px !important", height: "24px !important" }}
                                    placeholder="Age"
                                    name="PensionAge"
                                    value={values.PensionAge || ""}
                                    onChange={(e) => {
                                      const age = e.target.value;
                                      setFieldValue("PensionAge", age);
                                      if (values.DateOfBirth && age && Number(age) > 0) {
                                        setFieldValue("DateOfRetire", calculateRetirementDate(values.DateOfBirth, age));
                                      }
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                            <div className="col-12 col-md-6 col-xl-2">
                              <label className="text-label fw-bold">Date of Birth:</label>
                              <div className="d-flex align-items-center gap-1">
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  name="DateOfBirth"
                                  value={values.DateOfBirth ? values.DateOfBirth.split("T")[0] : ""}
                                  onBlur={handleBlur}
                                  onChange={(e) => {
                                    handleChange(e);
                                    const dobVal = e.target.value;
                                    const ageToUse = values.IsDeferredPension && values.PensionAge ? Number(values.PensionAge) : (retirementAge || 58);
                                    if (dobVal) {
                                      const retireDate = calculateRetirementDate(dobVal, ageToUse);
                                      setFieldValue("DateOfRetire", retireDate);
                                    } else {
                                      setFieldValue("DateOfRetire", "");
                                    }
                                  }}
                                />
                                {currentAge !== "" && (
                                  <span
                                    className="badge px-1.5 py-1 fw-bold text-dark"
                                    style={{ backgroundColor: "#00e5ff", fontSize: "10.5px", borderRadius: "3px", minWidth: "28px", textAlign: "center", lineHeight: "1" }}
                                    title="Calculated Age"
                                  >
                                    {currentAge}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="col-12 col-md-6 col-xl-2">
                              <label className="text-label fw-bold">Date of Retire:</label>
                              <input
                                type="date"
                                className="form-control form-control-sm bg-light"
                                name="DateOfRetire"
                                value={values.DateOfRetire ? values.DateOfRetire.split("T")[0] : ""}
                                readOnly
                                disabled
                              />
                            </div>
                          </div>

                          {/* Row 3: Bank Payment | Shift | Country | Permanent Address | Present Address */}
                          <div className="row gx-2 gy-1 mb-1 align-items-end">
                            <div className="col-6 col-md-2 col-xl-1">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="BankPayment"
                                  name="BankPayment"
                                  checked={!!values.BankPayment}
                                  onChange={(e) => setFieldValue("BankPayment", e.target.checked)}
                                />
                                <label htmlFor="BankPayment" className="form-check-label">Bank Pay</label>
                              </div>
                            </div>
                            <div className="col-6 col-md-3 col-xl-2">
                              <label className="text-label fw-bold">Shift :</label>
                              <select
                                className="form-control form-control-sm"
                                name="F_ShiftMaster"
                                value={values.F_ShiftMaster || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">-- Select Shift --</option>
                                {availableShifts.map((s) => (
                                  <option key={s.Id} value={s.Id}>{s.Name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-6 col-md-2 col-xl-1">
                              <label className="text-label fw-bold">Country :</label>
                              <select className="form-control form-control-sm" name="F_CountryMaster" value={values.F_CountryMaster || 1} onChange={handleChange} onBlur={handleBlur}>
                                {(state.CountryMaster || []).map((c) => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
                              </select>
                            </div>
                            <div className="col-12 col-md-5 col-xl-4">
                              <label className="text-label fw-bold">Permanent Address:</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                name="PermanentAddress"
                                value={values.PermanentAddress || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Permanent Address"
                              />
                            </div>
                            <div className="col-12 col-md-6 col-xl-4">
                              <label className="text-label fw-bold">Present Address:</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                name="PresentAddress"
                                value={values.PresentAddress || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Present Address"
                              />
                            </div>
                          </div>

                          {/* Row 4: Mobile No | Email | Ed Qualification | Is Handicap | Handicap Category | Status | Aadhar Number */}
                          <div className="row gx-2 gy-1 mb-1 align-items-end">
                            <div className="col-6 col-md-3 col-xl-2">
                              <label className="text-label fw-bold">Mobile No.:</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                name="MobileNo"
                                maxLength={10}
                                placeholder="10-digit Mobile"
                                value={values.MobileNo || ""}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                  setFieldValue("MobileNo", val);
                                }}
                                onBlur={handleBlur}
                              />
                              {errors.MobileNo && <div className="text-danger fs-11">{errors.MobileNo}</div>}
                            </div>
                            <div className="col-6 col-md-3 col-xl-2">
                              <label className="text-label fw-bold">EMail :</label>
                              <input
                                type="email"
                                className="form-control form-control-sm"
                                name="Email"
                                placeholder="Email Address"
                                value={values.Email || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {errors.Email && <div className="text-danger fs-11">{errors.Email}</div>}
                            </div>
                            <div className="col-6 col-md-3 col-xl-2">
                              <label className="text-label fw-bold">Ed. Qualification :</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                name="EdQualification"
                                list="educationOptions"
                                placeholder="Qualification"
                                value={values.EdQualification || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <datalist id="educationOptions">
                                <option value="Below 10th" />
                                <option value="10th (Secondary)" />
                                <option value="12th (Higher Secondary)" />
                                <option value="ITI / Diploma" />
                                <option value="Graduate (BA/BSc/BCom/BTech)" />
                                <option value="Post Graduate (MA/MSc/MCom/MBA)" />
                                <option value="Illiterate" />
                              </datalist>
                            </div>
                            <div className="col-6 col-md-3 col-xl-1">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="IsHandicap"
                                  name="IsHandicap"
                                  checked={!!values.IsHandicap}
                                  onChange={(e) => {
                                    const val = e.target.checked;
                                    setFieldValue("IsHandicap", val);
                                    if (!val) {
                                      setFieldValue("HandicapCategory", "");
                                    }
                                  }}
                                />
                                <label htmlFor="IsHandicap" className="form-check-label">Handicap</label>
                              </div>
                            </div>
                            {values.IsHandicap && (
                              <div className="col-6 col-md-3 col-xl-2">
                                <label className="text-label fw-bold">Handicap Category:</label>
                                <select
                                  className="form-control form-control-sm"
                                  name="HandicapCategory"
                                  value={values.HandicapCategory || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                >
                                  <option value="">-- Category --</option>
                                  <option value="Locomotor Disability">Locomotor Disability</option>
                                  <option value="Visual Impairment">Visual Impairment</option>
                                  <option value="Hearing Impairment">Hearing Impairment</option>
                                  <option value="Mental Illness / Intellectual">Mental Illness / Intellectual</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>
                            )}
                            <div className="col-6 col-md-2 col-xl-1">
                              <label className="text-label fw-bold">Status:</label>
                              <select className="form-control form-control-sm" name="Status" value={values.Status || "Working"} onChange={handleChange} onBlur={handleBlur}>
                                <option value="Working">Working</option>
                                <option value="Left">Left</option>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                                <option value="Suspended">Suspended</option>
                                <option value="Retired">Retired</option>
                              </select>
                            </div>
                            <div className="col-12 col-md-4 col-xl-2">
                              <label className="text-label fw-bold">Aadhar Number :</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                name="AadharNumber"
                                maxLength={12}
                                placeholder="12-digit Aadhar"
                                value={values.AadharNumber || ""}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, "").slice(0, 12);
                                  setFieldValue("AadharNumber", val);
                                }}
                                onBlur={handleBlur}
                              />
                            </div>
                          </div>

                          {/* Row 5: Name in Aadhar | Marital Status | Married | Refund File Date | Refunded | Gratuity Paid Date | Remark */}
                          <div className="row gx-2 gy-1 mb-1 align-items-end">
                            <div className="col-12 col-md-4 col-xl-2">
                              <label className="text-label fw-bold">Name in Aadhar :</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                name="NameInAadhar"
                                placeholder="Name as on Aadhar"
                                value={values.NameInAadhar || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <div className="col-6 col-md-2 col-xl-1">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="MaritalStatus"
                                  name="MaritalStatus"
                                  checked={!!values.MaritalStatus}
                                  onChange={(e) => {
                                    const val = e.target.checked;
                                    setFieldValue("MaritalStatus", val);
                                    if (val && values.Married === "Unmarried") {
                                      setFieldValue("Married", "Married");
                                    }
                                  }}
                                />
                                <label htmlFor="MaritalStatus" className="form-check-label">Marital</label>
                              </div>
                            </div>
                            <div className="col-6 col-md-2 col-xl-1">
                              <label className="text-label fw-bold">Married :</label>
                              <select className="form-control form-control-sm" name="Married" value={values.Married || "Unmarried"} onChange={handleChange} onBlur={handleBlur}>
                                <option value="Unmarried">Unmarried</option>
                                <option value="Married">Married</option>
                                <option value="Widowed">Widowed</option>
                                <option value="Divorced">Divorced</option>
                              </select>
                            </div>
                            <div className="col-6 col-md-2 col-xl-1.5" style={{ width: "12.5%" }}>
                              <label className="text-label fw-bold text-truncate">Refund File Date:</label>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                name="RefundFileDate"
                                value={values.RefundFileDate ? values.RefundFileDate.split("T")[0] : ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <div className="col-6 col-md-1 col-xl-1">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="Refunded"
                                  name="Refunded"
                                  checked={!!values.Refunded}
                                  onChange={(e) => setFieldValue("Refunded", e.target.checked)}
                                />
                                <label htmlFor="Refunded" className="form-check-label">Refunded</label>
                              </div>
                            </div>
                            <div className="col-6 col-md-2 col-xl-1.5" style={{ width: "12.5%" }}>
                              <label className="text-label fw-bold text-truncate">Gratuity Paid Date:</label>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                name="GratuityPaidDate"
                                value={values.GratuityPaidDate ? values.GratuityPaidDate.split("T")[0] : ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <div className="col-12 col-md-5 col-xl-4">
                              <label className="text-label fw-bold">Remark:</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                name="Remark"
                                value={values.Remark || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Extra remarks"
                              />
                            </div>
                          </div>

                          {/* Row 6: Photo & Signature Upload Section (Compact Side-by-Side) */}
                          <div className="row gx-2 gy-1 mt-1 pt-1 border-top align-items-center">
                            <div className="col-12 col-md-6">
                              <div className="d-flex align-items-center gap-2 p-1 border rounded bg-light" style={{ minHeight: "42px" }}>
                                <div style={{ width: "36px", height: "36px", borderRadius: "4px", border: "1px dashed #bbb", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "#fff", flexShrink: 0 }}>
                                  {values.PhotoPreview || values.Photo ? (
                                    <img
                                      src={values.PhotoPreview || (values.Photo.startsWith("data:") || values.Photo.startsWith("http") ? values.Photo : `${API_WEB_URLS.IMAGEURL}${values.Photo}`)}
                                      alt="Photo Preview"
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <i className="fa fa-user text-muted" style={{ fontSize: "14px" }} />
                                  )}
                                </div>
                                <div className="flex-grow-1 d-flex align-items-center gap-1">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control form-control-sm py-0"
                                    style={{ height: "24px", fontSize: "10.5px" }}
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        setFieldValue("PhotoFile", file);
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                          setFieldValue("PhotoPreview", reader.result);
                                          setFieldValue("Photo", reader.result);
                                        };
                                        reader.readAsDataURL(file);
                                      }
                                    }}
                                  />
                                  {(values.PhotoPreview || values.Photo) && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-xs py-0 px-1"
                                      style={{ height: "24px", fontSize: "10px", lineHeight: "1" }}
                                      title="Remove Photo"
                                      onClick={() => {
                                        setFieldValue("PhotoFile", null);
                                        setFieldValue("PhotoPreview", "");
                                        setFieldValue("Photo", "");
                                      }}
                                    >
                                      <i className="fa fa-trash" />
                                    </button>
                                  )}
                                </div>
                                <span className="text-muted fw-bold ms-1" style={{ fontSize: "10.5px", whiteSpace: "nowrap" }}>Photo</span>
                              </div>
                            </div>

                            <div className="col-12 col-md-6">
                              <div className="d-flex align-items-center gap-2 p-1 border rounded bg-light" style={{ minHeight: "42px" }}>
                                <div style={{ width: "55px", height: "36px", borderRadius: "4px", border: "1px dashed #bbb", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "#fff", flexShrink: 0 }}>
                                  {values.SignaturePreview || values.Signature ? (
                                    <img
                                      src={values.SignaturePreview || (values.Signature.startsWith("data:") || values.Signature.startsWith("http") ? values.Signature : `${API_WEB_URLS.IMAGEURL}${values.Signature}`)}
                                      alt="Signature Preview"
                                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                    />
                                  ) : (
                                    <i className="fa fa-pencil text-muted" style={{ fontSize: "14px" }} />
                                  )}
                                </div>
                                <div className="flex-grow-1 d-flex align-items-center gap-1">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control form-control-sm py-0"
                                    style={{ height: "24px", fontSize: "10.5px" }}
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        setFieldValue("SignatureFile", file);
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                          setFieldValue("SignaturePreview", reader.result);
                                          setFieldValue("Signature", reader.result);
                                        };
                                        reader.readAsDataURL(file);
                                      }
                                    }}
                                  />
                                  {(values.SignaturePreview || values.Signature) && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-xs py-0 px-1"
                                      style={{ height: "24px", fontSize: "10px", lineHeight: "1" }}
                                      title="Remove Signature"
                                      onClick={() => {
                                        setFieldValue("SignatureFile", null);
                                        setFieldValue("SignaturePreview", "");
                                        setFieldValue("Signature", "");
                                      }}
                                    >
                                      <i className="fa fa-trash" />
                                    </button>
                                  )}
                                </div>
                                <span className="text-muted fw-bold ms-1" style={{ fontSize: "10.5px", whiteSpace: "nowrap" }}>Signature</span>
                              </div>
                            </div>
                          </div>

                        </div>
                      )}

                      {/* ════════════════════════════════════════════════════════════════════════
                          TAB 2: BASIC (Salary & Compliance)
                          ════════════════════════════════════════════════════════════════════════ */}
                      {activeTab === "salary" && (
                        <div className="tab-pane-content emp-compact-tab">
                          
                          {/* 1. Top Salary Grid */}
                          <div className="table-responsive mb-1" style={{ maxHeight: "140px" }}>
                            <table className="table table-bordered table-sm align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: 30 }} className="text-center">#</th>
                                  <th>Basic Salary</th>
                                  <th>Effective Date</th>
                                  <th>Basic Type *</th>
                                  <th style={{ width: 70 }} className="text-center">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {salaryRows.map((row, i) => (
                                  <tr key={i}>
                                    <td className="align-middle text-center">{i + 1}</td>
                                    <td>
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="form-control form-control-sm"
                                        placeholder="0.00"
                                        value={row.BasicSalary}
                                        onChange={(e) => changeSalaryRow(i, "BasicSalary", e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={row.EffectiveDate ? row.EffectiveDate.split("T")[0] : ""}
                                        onChange={(e) => changeSalaryRow(i, "EffectiveDate", e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="form-check form-check-inline mb-0">
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            name={`basicType_${i}`}
                                            id={`basicTypeMonthly_${i}`}
                                            value={1}
                                            checked={Number(row.F_BasicTypeMaster) === 1 || !row.F_BasicTypeMaster}
                                            onChange={() => changeSalaryRow(i, "F_BasicTypeMaster", 1)}
                                          />
                                          <label className="form-check-label" htmlFor={`basicTypeMonthly_${i}`}>Monthly</label>
                                        </div>
                                        <div className="form-check form-check-inline mb-0">
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            name={`basicType_${i}`}
                                            id={`basicTypePerDay_${i}`}
                                            value={2}
                                            checked={Number(row.F_BasicTypeMaster) === 2}
                                            onChange={() => changeSalaryRow(i, "F_BasicTypeMaster", 2)}
                                          />
                                          <label className="form-check-label" htmlFor={`basicTypePerDay_${i}`}>Per Day</label>
                                        </div>
                                      </div>
                                    </td>
                                    <ActionCell
                                      index={i}
                                      rows={salaryRows}
                                      onAdd={() => {
                                        const monthly = state.BasicTypeMaster?.find(b => b.Id === 1 || String(b.Name).toLowerCase().includes("month"));
                                        const defaultId = monthly ? monthly.Id : 1;
                                        const deptDate = deptRows?.[0]?.EffectiveDate ? deptRows[0].EffectiveDate.split("T")[0] : "";
                                        addRow(setSalaryRows, () => ({ ...emptySalaryRow(defaultId), EffectiveDate: deptDate }));
                                      }}
                                      onRemove={(idx) => removeRow(setSalaryRows, idx)}
                                    />
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* 2. Compliance Section (ESI & PF) */}
                          <div className="card mb-1 border shadow-none" style={{ background: "#f8fbff", borderRadius: 4, padding: "5px 8px" }}>
                            
                            {/* ESI Row */}
                            <div className="row gx-2 gy-1 align-items-center mb-1 pb-1 border-bottom">
                              <div className="col-auto">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="ESIApplicable"
                                    checked={!!values.ESIApplicable}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;
                                      setFieldValue("ESIApplicable", isChecked);
                                      if (isChecked) {
                                        const deptEffDate = deptRows?.[0]?.EffectiveDate ? deptRows[0].EffectiveDate.split("T")[0] : "";
                                        if (deptEffDate && (!values.ESIJoiningDate || values.ESIJoiningDate === "")) {
                                          setFieldValue("ESIJoiningDate", deptEffDate);
                                        }
                                      }
                                    }}
                                  />
                                  <label htmlFor="ESIApplicable" className="form-check-label fw-bold text-primary">ESI Applicable</label>
                                </div>
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>ESI No:</span>
                                {isMultiESI && esiNoList.length > 0 ? (
                                  <select
                                    className="form-control form-control-sm"
                                    name="ESINo"
                                    value={values.ESINo || ""}
                                    onChange={handleChange}
                                  >
                                    <option value="">-- Select --</option>
                                    {esiNoList.map(no => (
                                      <option key={no} value={no}>{no}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input type="text" className="form-control form-control-sm" name="ESINo" value={values.ESINo || ""} onChange={handleChange} />
                                )}
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Join Date:</span>
                                <input type="date" className="form-control form-control-sm" name="ESIJoiningDate" value={values.ESIJoiningDate || ""} onChange={handleChange} />
                              </div>
                              <div className="col-auto d-flex flex-wrap align-items-center gap-2">
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="ESIOldEmployee" checked={!!values.ESIOldEmployee} onChange={e => setFieldValue("ESIOldEmployee", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor="ESIOldEmployee">Old Emp</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="TICs" checked={!!values.TICs} onChange={e => setFieldValue("TICs", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor="TICs">TICs</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="PICs" checked={!!values.PICs} onChange={e => setFieldValue("PICs", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor="PICs">PICs</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="Accident" checked={!!values.Accident} onChange={e => setFieldValue("Accident", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor="Accident">Accident</label></div>
                              </div>
                            </div>

                            {/* PF Section */}
                            <div className="row gx-2 gy-1 align-items-center mb-1 pb-1 border-bottom">
                              <div className="col-auto">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="PFApplicable"
                                    checked={!!values.PFApplicable}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;
                                      setFieldValue("PFApplicable", isChecked);
                                      if (isChecked) {
                                        if (!values.PensionApplicable) {
                                          setFieldValue("PensionApplicable", true);
                                        }
                                        const deptEffDate = deptRows?.[0]?.EffectiveDate ? deptRows[0].EffectiveDate.split("T")[0] : "";
                                        if (deptEffDate && (!values.PFJoinDate || values.PFJoinDate === "")) {
                                          setFieldValue("PFJoinDate", deptEffDate);
                                        }
                                      }
                                    }}
                                  />
                                  <label htmlFor="PFApplicable" className="form-check-label fw-bold text-primary">PF Applicable</label>
                                </div>
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Join Date:</span>
                                <input type="date" className="form-control form-control-sm" name="PFJoinDate" value={values.PFJoinDate || ""} onChange={handleChange} />
                              </div>
                              <div className="col-auto d-flex flex-wrap align-items-center gap-2">
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="EmplrPFOverLimit" checked={!!values.EmplrPFOverLimit} onChange={e => setFieldValue("EmplrPFOverLimit", e.target.checked)} /><label className="form-check-label" htmlFor="EmplrPFOverLimit">Emplr Over Lim</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="EmpleeDedOverLimit" checked={!!values.EmpleeDedOverLimit} onChange={e => setFieldValue("EmpleeDedOverLimit", e.target.checked)} /><label className="form-check-label" htmlFor="EmpleeDedOverLimit">Emplee Over Lim</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="PensionApplicable" checked={!!values.PensionApplicable} onChange={e => setFieldValue("PensionApplicable", e.target.checked)} /><label className="form-check-label" htmlFor="PensionApplicable">Pension</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="PensionOverLimit" checked={!!values.PensionOverLimit} onChange={e => setFieldValue("PensionOverLimit", e.target.checked)} /><label className="form-check-label" htmlFor="PensionOverLimit">Pension Over Lim</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="HigherPension" checked={!!values.HigherPension} onChange={e => setFieldValue("HigherPension", e.target.checked)} /><label className="form-check-label" htmlFor="HigherPension">Higher Pension</label></div>
                              </div>
                            </div>

                            {/* PF Row 1: PF No | UAN | Extra PF | Calc Type | Fixed PF Salary */}
                            <div className="row gx-2 gy-1 align-items-center mb-1">
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>PF No:</span>
                                {isMultiPF && pfNoList.length > 0 ? (
                                  <select
                                    className="form-control form-control-sm"
                                    name="PFNo"
                                    value={values.PFNo || ""}
                                    onChange={handleChange}
                                  >
                                    <option value="">-- Select --</option>
                                    {pfNoList.map(no => (
                                      <option key={no} value={no}>{no}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input type="text" className="form-control form-control-sm" name="PFNo" value={values.PFNo || ""} onChange={handleChange} />
                                )}
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>UAN:</span>
                                <input type="text" className="form-control form-control-sm" name="UAN" value={values.UAN || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Extra PF:</span>
                                <input type="number" step="0.01" className="form-control form-control-sm" name="ExtraPF" value={values.ExtraPF || ""} onChange={handleChange} />
                              </div>
                              <div className="col-auto d-flex align-items-center gap-2">
                                <div className="form-check mb-0"><input className="form-check-input" type="radio" name="PFCalculationType" id="pfCalcPerc" value="Percentage" checked={values.PFCalculationType === "Percentage"} onChange={handleChange} /><label className="form-check-label" htmlFor="pfCalcPerc">%</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="radio" name="PFCalculationType" id="pfCalcManual" value="Manual" checked={values.PFCalculationType === "Manual"} onChange={handleChange} /><label className="form-check-label" htmlFor="pfCalcManual">Manual</label></div>
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Fixed PF Sal:</span>
                                <input type="number" step="0.01" className="form-control form-control-sm" name="FixedPFSalary" value={values.FixedPFSalary || ""} onChange={handleChange} />
                              </div>
                            </div>

                            {/* PF Row 2: Old Member, Prev PF, Old UAN, Exit Date, PMRPY, PMGKY, ABRY */}
                            <div className="row gx-2 gy-1 align-items-center">
                              <div className="col-auto d-flex align-items-center gap-2">
                                <span className="fw-bold text-dark" style={{ fontSize: 11 }}>Old Member:</span>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="OldMemberPF" checked={!!values.OldMemberPF} onChange={e => setFieldValue("OldMemberPF", e.target.checked)} /><label className="form-check-label" htmlFor="OldMemberPF">PF</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="OldMemberPension" checked={!!values.OldMemberPension} onChange={e => setFieldValue("OldMemberPension", e.target.checked)} /><label className="form-check-label" htmlFor="OldMemberPension">Pension</label></div>
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Prev PF:</span>
                                <input type="text" className="form-control form-control-sm" name="PrevPFNo" value={values.PrevPFNo || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Old UAN:</span>
                                <input type="text" className="form-control form-control-sm" name="OldUAN" value={values.OldUAN || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Exit Date:</span>
                                <input type="date" className="form-control form-control-sm" name="DateOfExitPr" value={values.DateOfExitPr || ""} onChange={handleChange} />
                              </div>
                              <div className="col-auto d-flex flex-wrap align-items-center gap-2 ms-auto">
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="PMRPY" checked={!!values.PMRPY} onChange={e => setFieldValue("PMRPY", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor="PMRPY">PMRPY</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="PMGKY" checked={!!values.PMGKY} onChange={e => setFieldValue("PMGKY", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor="PMGKY">PMGKY</label></div>
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="ABRY" checked={!!values.ABRY} onChange={e => setFieldValue("ABRY", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor="ABRY">ABRY</label></div>
                              </div>
                            </div>

                          </div>

                          {/* 3. Bank Details & Holiday Section */}
                          <div className="card mb-1 border shadow-none" style={{ background: "#fdfefe", borderRadius: 4, padding: "5px 8px" }}>
                            <div className="row gx-2 gy-1 align-items-center">
                              <div className="col-6 col-md-2 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Holiday:</span>
                                <select className="form-control form-control-sm" name="WeeklyHoliday" value={values.WeeklyHoliday || "Sunday"} onChange={handleChange}>
                                  <option value="Sunday">Sunday</option>
                                  <option value="Monday">Monday</option>
                                  <option value="Tuesday">Tuesday</option>
                                  <option value="Wednesday">Wednesday</option>
                                  <option value="Thursday">Thursday</option>
                                  <option value="Friday">Friday</option>
                                  <option value="Saturday">Saturday</option>
                                </select>
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Bank Name:</span>
                                <input type="text" className="form-control form-control-sm" name="BankName" placeholder="Bank Name" value={values.BankName || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Branch:</span>
                                <input type="text" className="form-control form-control-sm" name="BankBranch" placeholder="Branch" value={values.BankBranch || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-2 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>IFSC:</span>
                                <input type="text" className="form-control form-control-sm" name="IFSCCode" placeholder="IFSC Code" value={values.IFSCCode || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-2 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>A/c No.:</span>
                                <input type="text" className="form-control form-control-sm" name="BankAccountNo" placeholder="Account No" value={values.BankAccountNo || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-2 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>In Bank:</span>
                                <input type="text" className="form-control form-control-sm" name="NameAsBank" placeholder="Name as in Bank" value={values.NameAsBank || ""} onChange={handleChange} />
                              </div>
                            </div>
                          </div>

                          {/* 4. Identity & Documents Section (PAN, DL, Passport, Intl. Emp) */}
                          <div className="card mb-1 border shadow-none" style={{ background: "#f8f9fa", borderRadius: 4, padding: "5px 8px" }}>
                            {/* Row 1: PAN & Driving Licence */}
                            <div className="row gx-2 gy-1 align-items-center mb-1 pb-1 border-bottom">
                              <div className="col-6 col-md-2 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>PAN No:</span>
                                <input type="text" className="form-control form-control-sm" name="PanCardNo" placeholder="PAN No" value={values.PanCardNo || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>In PAN:</span>
                                <input type="text" className="form-control form-control-sm" name="NameAsPerPan" placeholder="PAN Name" value={values.NameAsPerPan || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-2 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>DL No.:</span>
                                <input type="text" className="form-control form-control-sm" name="DrivingLicenceNo" placeholder="DL No" value={values.DrivingLicenceNo || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-2 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>DL Expiry:</span>
                                <input type="date" className="form-control form-control-sm" name="DLExpiryDate" value={values.DLExpiryDate || ""} onChange={handleChange} />
                              </div>
                              <div className="col-12 col-md-3 col-xl-4 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>In DL:</span>
                                <input type="text" className="form-control form-control-sm" name="NameAsPerDL" placeholder="DL Name" value={values.NameAsPerDL || ""} onChange={handleChange} />
                              </div>
                            </div>

                            {/* Row 2: Passport & International Employee */}
                            <div className="row gx-2 gy-1 align-items-center">
                              <div className="col-6 col-md-2 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Passport:</span>
                                <input type="text" className="form-control form-control-sm" name="PassportNo" placeholder="Passport No" value={values.PassportNo || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-2 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>From:</span>
                                <input type="date" className="form-control form-control-sm" name="PassportFrom" value={values.PassportFrom || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-2 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Till:</span>
                                <input type="date" className="form-control form-control-sm" name="PassportTill" value={values.PassportTill || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>In Passport:</span>
                                <input type="text" className="form-control form-control-sm" name="PassportName" placeholder="Passport Name" value={values.PassportName || ""} onChange={handleChange} />
                              </div>
                              <div className="col-auto">
                                <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="IsInternationalEmployee" checked={!!values.IsInternationalEmployee} onChange={e => setFieldValue("IsInternationalEmployee", e.target.checked)} /><label className="form-check-label fw-bold" htmlFor="IsInternationalEmployee">Intl. Emp</label></div>
                              </div>
                              <div className="col-6 col-md-2 col-xl-1 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Origin:</span>
                                <input type="text" className="form-control form-control-sm" name="CountryOrigin" value={values.CountryOrigin || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-md-2 col-xl-1 d-flex align-items-center gap-1">
                                <span className="text-label fw-bold mb-0 text-nowrap" style={{ fontSize: 11 }}>Nation:</span>
                                <input type="text" className="form-control form-control-sm" name="Nationality" value={values.Nationality || ""} onChange={handleChange} />
                              </div>
                            </div>
                          </div>

                        </div>
                      )}

                      {/* ════════════════════════════════════════════════════════════════════════
                          TAB 3: KYC
                          ════════════════════════════════════════════════════════════════════════ */}
                      {activeTab === "kyc" && (
                        <div className="tab-pane-content">
                          
                          {/* Top KYC Fields Form */}
                          <div className="card mb-2 border shadow-none bg-light">
                            <div className="card-body py-1 px-2">
                              <div className="row gx-2 gy-1 align-items-center">
                                <div className="col-12 col-md-3">
                                  <label className="text-label fw-bold mb-0">Voter ID No.:</label>
                                  <input type="text" className="form-control form-control-sm" name="VoterIDNo" value={values.VoterIDNo || ""} onChange={handleChange} />
                                </div>
                                <div className="col-12 col-md-3">
                                  <label className="text-label fw-bold mb-0">Name in Voter ID:</label>
                                  <input type="text" className="form-control form-control-sm" name="NameAsPerVoterID" value={values.NameAsPerVoterID || ""} onChange={handleChange} />
                                </div>
                                <div className="col-12 col-md-3">
                                  <label className="text-label fw-bold mb-0">Rasan Card No.:</label>
                                  <input type="text" className="form-control form-control-sm" name="RationCardNo" value={values.RationCardNo || ""} onChange={handleChange} />
                                </div>
                                <div className="col-12 col-md-3">
                                  <label className="text-label fw-bold mb-0">Name in Ration Card:</label>
                                  <input type="text" className="form-control form-control-sm" name="NameAsPerRationCard" value={values.NameAsPerRationCard || ""} onChange={handleChange} />
                                </div>
                                <div className="col-12 col-md-3">
                                  <label className="text-label fw-bold mb-0">National Population No.:</label>
                                  <input type="text" className="form-control form-control-sm" name="NationalPopulationNo" value={values.NationalPopulationNo || ""} onChange={handleChange} />
                                </div>
                                <div className="col-12 col-md-3">
                                  <label className="text-label fw-bold mb-0">Name in NPR:</label>
                                  <input type="text" className="form-control form-control-sm" name="NameAsPerNPR" value={values.NameAsPerNPR || ""} onChange={handleChange} />
                                </div>
                                <div className="col-12 col-md-3">
                                  <label className="text-label fw-bold mb-0">Opening Balance Salary:</label>
                                  <input type="number" step="0.01" className="form-control form-control-sm" name="OpeningBalanceSalary" value={values.OpeningBalanceSalary || ""} onChange={handleChange} placeholder="0.00" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Nominee Table (With Photo Column) */}
                          <div className="mb-2">
                            <h6 className="fw-bold text-primary mb-1" style={{ fontSize: "11.5px" }}>Nominee Details</h6>
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm align-middle mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: 30 }} className="text-center">#</th>
                                    <th style={{ width: 50 }} className="text-center">Photo</th>
                                    <th>NomineeName</th>
                                    <th>Address</th>
                                    <th>Relationship</th>
                                    <th>Birth Date</th>
                                    <th>Amount (%)</th>
                                    <th>Parent Name</th>
                                    <th>Aadhar No</th>
                                    <th style={{ width: 70 }} className="text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {nomineeRows.map((row, i) => (
                                    <tr key={i}>
                                      <td className="align-middle text-center">{i + 1}</td>
                                      <td className="align-middle text-center" style={{ minWidth: 50 }}>
                                        <div className="d-flex flex-column align-items-center justify-content-center">
                                          {row.Photo ? (
                                            <div className="position-relative" style={{ width: 28, height: 28 }}>
                                              <img
                                                src={row.Photo.startsWith("data:") || row.Photo.startsWith("http") ? row.Photo : `${API_WEB_URLS.IMAGEURL}${row.Photo}`}
                                                alt="Nominee"
                                                style={{ width: 28, height: 28, objectFit: "cover", borderRadius: "3px", border: "1px solid #ccc" }}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-danger btn-xs position-absolute"
                                                style={{ top: -5, right: -6, padding: "0 3px", fontSize: "8px", borderRadius: "50%", lineHeight: 1 }}
                                                title="Remove photo"
                                                onClick={() => changeRow(setNomineeRows, i, "Photo", "")}
                                              >
                                                ×
                                              </button>
                                            </div>
                                          ) : (
                                            <label className="btn btn-outline-primary btn-xs mb-0 py-0 px-1" style={{ fontSize: "10px", cursor: "pointer" }} title="Upload Photo">
                                              <i className="fa fa-camera" />
                                              <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: "none" }}
                                                onChange={(e) => {
                                                  const file = e.target.files[0];
                                                  if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                      changeRow(setNomineeRows, i, "Photo", reader.result);
                                                    };
                                                    reader.readAsDataURL(file);
                                                  }
                                                }}
                                              />
                                            </label>
                                          )}
                                        </div>
                                      </td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.NomineeName} onChange={(e) => changeRow(setNomineeRows, i, "NomineeName", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.Address} onChange={(e) => changeRow(setNomineeRows, i, "Address", e.target.value)} /></td>
                                      <td>
                                        <select className="form-control form-control-sm" value={row.F_RelationshipMaster} onChange={(e) => changeRow(setNomineeRows, i, "F_RelationshipMaster", e.target.value)}>
                                          <option value="">-- Select --</option>
                                          {state.RelationshipMaster.map((r) => <option key={r.Id} value={r.Id}>{r.Name}</option>)}
                                        </select>
                                      </td>
                                      <td><input type="date" className="form-control form-control-sm" value={row.BirthDate ? row.BirthDate.split("T")[0] : ""} onChange={(e) => changeRow(setNomineeRows, i, "BirthDate", e.target.value)} /></td>
                                      <td><input type="number" step="0.01" className="form-control form-control-sm" value={row.Amount} onChange={(e) => changeRow(setNomineeRows, i, "Amount", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.ParentName} onChange={(e) => changeRow(setNomineeRows, i, "ParentName", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.AadharNo} onChange={(e) => changeRow(setNomineeRows, i, "AadharNo", e.target.value)} /></td>
                                      <ActionCell index={i} rows={nomineeRows} onAdd={() => addRow(setNomineeRows, emptyNomineeRow)} onRemove={(idx) => removeRow(setNomineeRows, idx)} />
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Relative / Family Table */}
                          <div>
                            <h6 className="fw-bold text-primary mb-1" style={{ fontSize: "11.5px" }}>Relative / Family Details</h6>
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm align-middle mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: 30 }} className="text-center">#</th>
                                    <th style={{ width: 50 }} className="text-center">Photo</th>
                                    <th>RelativeName</th>
                                    <th>DateofBirth</th>
                                    <th>Address</th>
                                    <th>Relationship</th>
                                    <th style={{ width: 70 }} className="text-center">Living with</th>
                                    <th style={{ width: 60 }} className="text-center">For ESI</th>
                                    <th>Aadhar</th>
                                    <th style={{ width: 70 }} className="text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {relativeRows.map((row, i) => (
                                    <tr key={i}>
                                      <td className="align-middle text-center">{i + 1}</td>
                                      <td className="align-middle text-center" style={{ minWidth: 50 }}>
                                        <div className="d-flex flex-column align-items-center justify-content-center">
                                          {row.Photo ? (
                                            <div className="position-relative" style={{ width: 28, height: 28 }}>
                                              <img
                                                src={row.Photo.startsWith("data:") || row.Photo.startsWith("http") ? row.Photo : `${API_WEB_URLS.IMAGEURL}${row.Photo}`}
                                                alt="Relative"
                                                style={{ width: 28, height: 28, objectFit: "cover", borderRadius: "3px", border: "1px solid #ccc" }}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-danger btn-xs position-absolute"
                                                style={{ top: -5, right: -6, padding: "0 3px", fontSize: "8px", borderRadius: "50%", lineHeight: 1 }}
                                                title="Remove photo"
                                                onClick={() => changeRow(setRelativeRows, i, "Photo", "")}
                                              >
                                                ×
                                              </button>
                                            </div>
                                          ) : (
                                            <label className="btn btn-outline-primary btn-xs mb-0 py-0 px-1" style={{ fontSize: "10px", cursor: "pointer" }} title="Upload Photo">
                                              <i className="fa fa-camera" />
                                              <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: "none" }}
                                                onChange={(e) => {
                                                  const file = e.target.files[0];
                                                  if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                      changeRow(setRelativeRows, i, "Photo", reader.result);
                                                    };
                                                    reader.readAsDataURL(file);
                                                  }
                                                }}
                                              />
                                            </label>
                                          )}
                                        </div>
                                      </td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.RelativeName} onChange={(e) => changeRow(setRelativeRows, i, "RelativeName", e.target.value)} /></td>
                                      <td><input type="date" className="form-control form-control-sm" value={row.DateOfBirth ? row.DateOfBirth.split("T")[0] : ""} onChange={(e) => changeRow(setRelativeRows, i, "DateOfBirth", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.Address} onChange={(e) => changeRow(setRelativeRows, i, "Address", e.target.value)} /></td>
                                      <td>
                                        <select className="form-control form-control-sm" value={row.F_RelationshipMaster} onChange={(e) => changeRow(setRelativeRows, i, "F_RelationshipMaster", e.target.value)}>
                                          <option value="">-- Select --</option>
                                          {state.RelationshipMaster.map((r) => <option key={r.Id} value={r.Id}>{r.Name}</option>)}
                                        </select>
                                      </td>
                                      <td className="text-center align-middle">
                                        <input type="checkbox" checked={row.LivingWith === 1 || row.LivingWith === true} onChange={(e) => changeRow(setRelativeRows, i, "LivingWith", e.target.checked ? 1 : 0)} />
                                      </td>
                                      <td className="text-center align-middle">
                                        <input type="checkbox" checked={row.ForESI === 1 || row.ForESI === true} onChange={(e) => changeRow(setRelativeRows, i, "ForESI", e.target.checked ? 1 : 0)} />
                                      </td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.AadharNo} onChange={(e) => changeRow(setRelativeRows, i, "AadharNo", e.target.value)} /></td>
                                      <ActionCell index={i} rows={relativeRows} onAdd={() => addRow(setRelativeRows, emptyRelativeRow)} onRemove={(idx) => removeRow(setRelativeRows, idx)} />
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                        </div>
                      )}

                      {/* ════════════════════════════════════════════════════════════════════════
                          TAB 4: PENSION NOMINEE
                          ════════════════════════════════════════════════════════════════════════ */}
                      {activeTab === "pensionNominee" && (
                        <div className="tab-pane-content">
                          
                          {/* Pension Nominee Table (With Photo Column) */}
                          <div className="mb-2">
                            <h6 className="fw-bold text-primary mb-1" style={{ fontSize: "11.5px" }}>Pension Nominee Details</h6>
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm align-middle mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: 30 }} className="text-center">#</th>
                                    <th style={{ width: 50 }} className="text-center">Photo</th>
                                    <th>NomineeName</th>
                                    <th>Address</th>
                                    <th>Relationship</th>
                                    <th>Birth Date</th>
                                    <th>Amount Part</th>
                                    <th>Parent Name</th>
                                    <th>Aadhar No</th>
                                    <th>Name in Hindi</th>
                                    <th>Address Hindi</th>
                                    <th>Rel in Hindi</th>
                                    <th style={{ width: 70 }} className="text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pensionNomineeRows.map((row, i) => (
                                    <tr key={i}>
                                      <td className="align-middle text-center">{i + 1}</td>
                                      <td className="align-middle text-center" style={{ minWidth: 50 }}>
                                        <div className="d-flex flex-column align-items-center justify-content-center">
                                          {row.Photo ? (
                                            <div className="position-relative" style={{ width: 28, height: 28 }}>
                                              <img
                                                src={row.Photo.startsWith("data:") || row.Photo.startsWith("http") ? row.Photo : `${API_WEB_URLS.IMAGEURL}${row.Photo}`}
                                                alt="Pension Nominee"
                                                style={{ width: 28, height: 28, objectFit: "cover", borderRadius: "3px", border: "1px solid #ccc" }}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-danger btn-xs position-absolute"
                                                style={{ top: -5, right: -6, padding: "0 3px", fontSize: "8px", borderRadius: "50%", lineHeight: 1 }}
                                                title="Remove photo"
                                                onClick={() => changeRow(setPensionNomineeRows, i, "Photo", "")}
                                              >
                                                ×
                                              </button>
                                            </div>
                                          ) : (
                                            <label className="btn btn-outline-primary btn-xs mb-0 py-0 px-1" style={{ fontSize: "10px", cursor: "pointer" }} title="Upload Photo">
                                              <i className="fa fa-camera" />
                                              <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: "none" }}
                                                onChange={(e) => {
                                                  const file = e.target.files[0];
                                                  if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                      changeRow(setPensionNomineeRows, i, "Photo", reader.result);
                                                    };
                                                    reader.readAsDataURL(file);
                                                  }
                                                }}
                                              />
                                            </label>
                                          )}
                                        </div>
                                      </td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.NomineeName} onChange={(e) => changeRow(setPensionNomineeRows, i, "NomineeName", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.Address} onChange={(e) => changeRow(setPensionNomineeRows, i, "Address", e.target.value)} /></td>
                                      <td>
                                        <select className="form-control form-control-sm" value={row.F_RelationshipMaster} onChange={(e) => changeRow(setPensionNomineeRows, i, "F_RelationshipMaster", e.target.value)}>
                                          <option value="">-- Select --</option>
                                          {state.RelationshipMaster.map((r) => <option key={r.Id} value={r.Id}>{r.Name}</option>)}
                                        </select>
                                      </td>
                                      <td><input type="date" className="form-control form-control-sm" value={row.BirthDate ? row.BirthDate.split("T")[0] : ""} onChange={(e) => changeRow(setPensionNomineeRows, i, "BirthDate", e.target.value)} /></td>
                                      <td><input type="number" step="0.01" className="form-control form-control-sm" value={row.Amount} onChange={(e) => changeRow(setPensionNomineeRows, i, "Amount", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.ParentName} onChange={(e) => changeRow(setPensionNomineeRows, i, "ParentName", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.AadharNo} onChange={(e) => changeRow(setPensionNomineeRows, i, "AadharNo", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.NameHindi || ""} onChange={(e) => changeRow(setPensionNomineeRows, i, "NameHindi", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.AddressHindi || ""} onChange={(e) => changeRow(setPensionNomineeRows, i, "AddressHindi", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.RelHindi || ""} onChange={(e) => changeRow(setPensionNomineeRows, i, "RelHindi", e.target.value)} /></td>
                                      <ActionCell index={i} rows={pensionNomineeRows} onAdd={() => addRow(setPensionNomineeRows, emptyPensionNomineeRow)} onRemove={(idx) => removeRow(setPensionNomineeRows, idx)} />
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Two Side-by-Side Tables: Min Wages & Extra PF Rate */}
                          <div className="row gx-2 mb-2">
                            <div className="col-md-6 mb-1">
                              <div className="card h-100 border shadow-none mb-0">
                                <div className="card-header py-1 px-2 bg-light">
                                  <span className="mb-0 fw-bold" style={{ fontSize: "11px" }}>Min. Wages Table</span>
                                </div>
                                <div className="card-body p-1 table-responsive">
                                  <table className="table table-bordered table-sm align-middle mb-0">
                                    <thead className="table-light">
                                      <tr>
                                        <th style={{ width: 30 }} className="text-center">#</th>
                                        <th>Min. Wages</th>
                                        <th>Effective Date</th>
                                        <th style={{ width: 65 }} className="text-center">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {minWagesRows.map((row, i) => (
                                        <tr key={i}>
                                          <td className="text-center">{i + 1}</td>
                                          <td>
                                            <input
                                              type="number"
                                              step="0.01"
                                              className="form-control form-control-sm"
                                              value={row.MinWages}
                                              onChange={(e) => changeRow(setMinWagesRows, i, "MinWages", e.target.value)}
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="date"
                                              className="form-control form-control-sm"
                                              value={row.EffectiveDate ? row.EffectiveDate.split("T")[0] : ""}
                                              onChange={(e) => changeRow(setMinWagesRows, i, "EffectiveDate", e.target.value)}
                                            />
                                          </td>
                                          <ActionCell index={i} rows={minWagesRows} onAdd={() => addRow(setMinWagesRows, emptyMinWagesRow)} onRemove={(idx) => removeRow(setMinWagesRows, idx)} />
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6 mb-1">
                              <div className="card h-100 border shadow-none mb-0">
                                <div className="card-header py-1 px-2 bg-light">
                                  <span className="mb-0 fw-bold" style={{ fontSize: "11px" }}>Extra PF Rate Table</span>
                                </div>
                                <div className="card-body p-1 table-responsive">
                                  <table className="table table-bordered table-sm align-middle mb-0">
                                    <thead className="table-light">
                                      <tr>
                                        <th style={{ width: 30 }} className="text-center">#</th>
                                        <th>Extra PF Rate</th>
                                        <th style={{ width: 55 }} className="text-center">Manual</th>
                                        <th>Effective Date</th>
                                        <th style={{ width: 65 }} className="text-center">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {extraPFRateRows.map((row, i) => (
                                        <tr key={i}>
                                          <td className="text-center">{i + 1}</td>
                                          <td>
                                            <input
                                              type="number"
                                              step="0.01"
                                              className="form-control form-control-sm"
                                              value={row.ExtraPFRate}
                                              onChange={(e) => changeRow(setExtraPFRateRows, i, "ExtraPFRate", e.target.value)}
                                            />
                                          </td>
                                          <td className="text-center">
                                            <input
                                              type="checkbox"
                                              checked={!!row.IsManual}
                                              onChange={(e) => changeRow(setExtraPFRateRows, i, "IsManual", e.target.checked)}
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="date"
                                              className="form-control form-control-sm"
                                              value={row.EffectiveDate ? row.EffectiveDate.split("T")[0] : ""}
                                              onChange={(e) => changeRow(setExtraPFRateRows, i, "EffectiveDate", e.target.value)}
                                            />
                                          </td>
                                          <ActionCell index={i} rows={extraPFRateRows} onAdd={() => addRow(setExtraPFRateRows, emptyExtraPFRateRow)} onRemove={(idx) => removeRow(setExtraPFRateRows, idx)} />
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Bottom Opening Balance Fields */}
                          <div className="card border shadow-none bg-light mb-0">
                            <div className="card-body py-1 px-2">
                              <div className="row gx-2 gy-1 align-items-center">
                                <div className="col-12 col-md-4">
                                  <label className="text-label fw-bold mb-0">Employee Part Op.Bal:</label>
                                  <input type="number" step="0.01" className="form-control form-control-sm" name="EmployeePartOpBal" value={values.EmployeePartOpBal || ""} onChange={handleChange} placeholder="0.00" />
                                </div>
                                <div className="col-12 col-md-4">
                                  <label className="text-label fw-bold mb-0">Employer Part Op.Bal:</label>
                                  <input type="number" step="0.01" className="form-control form-control-sm" name="EmployerPartOpBal" value={values.EmployerPartOpBal || ""} onChange={handleChange} placeholder="0.00" />
                                </div>
                                <div className="col-12 col-md-4">
                                  <label className="text-label fw-bold mb-0">Year:</label>
                                  <input type="text" className="form-control form-control-sm" name="OpBalYear" value={values.OpBalYear || ""} onChange={handleChange} placeholder="e.g. 2024" />
                                </div>
                              </div>
                            </div>
                          </div>

                        </div>
                      )}

                      {/* ════════════════════════════════════════════════════════════════════════
                          OTHER TABS: Dept History, Shift History, Join History, Allowances, Deductions
                          ════════════════════════════════════════════════════════════════════════ */}
                      
                      {/* Dept History Tab */}
                      {activeTab === "department" && (
                        <div className="tab-pane-content table-responsive">
                          <table className="table table-bordered table-sm align-middle">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: 40 }} className="text-center">#</th>
                                <th>Department</th>
                                <th>Designation</th>
                                <th>Effective Date</th>
                                <th style={{ width: 85 }} className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deptRows.map((row, i) => (
                                <tr key={i}>
                                  <td className="align-middle text-center">{i + 1}</td>
                                  <td>
                                    <select className="form-control form-control-sm" value={row.F_DepartmentMaster} onChange={(e) => changeDeptRow(i, "F_DepartmentMaster", e.target.value, setFieldValue, values)}>
                                      <option value="">-- Select --</option>
                                      {state.DepartmentMaster.map((d) => <option key={d.Id} value={d.Id}>{d.Name}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <select className="form-control form-control-sm" value={row.F_DesignationMaster} onChange={(e) => changeDeptRow(i, "F_DesignationMaster", e.target.value, setFieldValue, values)}>
                                      <option value="">-- Select --</option>
                                      {state.DesignationMaster.map((d) => <option key={d.Id} value={d.Id}>{d.Name}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <input type="date" className="form-control form-control-sm" value={row.EffectiveDate ? row.EffectiveDate.split("T")[0] : ""} onChange={(e) => changeDeptRow(i, "EffectiveDate", e.target.value, setFieldValue, values)} />
                                  </td>
                                  <ActionCell index={i} rows={deptRows} onAdd={() => addRow(setDeptRows, emptyDeptRow)} onRemove={(idx) => removeRow(setDeptRows, idx)} />
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Shift History Tab */}
                      {activeTab === "shift" && (
                        <div className="tab-pane-content table-responsive">
                          <table className="table table-bordered table-sm align-middle">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: 40 }} className="text-center">#</th>
                                <th>Shift</th>
                                <th>Effective From</th>
                                <th style={{ width: 85 }} className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {shiftRows.map((row, i) => (
                                <tr key={i}>
                                  <td className="align-middle text-center">{i + 1}</td>
                                  <td>
                                    <select className="form-control form-control-sm" value={row.F_ShiftMaster} onChange={(e) => changeRow(setShiftRows, i, "F_ShiftMaster", e.target.value)}>
                                      <option value="">-- Select --</option>
                                      {availableShifts.map((s) => (
                                        <option key={s.Id} value={s.Id}>{s.Name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <input type="date" className="form-control form-control-sm" value={row.EffectiveFrom ? row.EffectiveFrom.split("T")[0] : ""} onChange={(e) => changeRow(setShiftRows, i, "EffectiveFrom", e.target.value)} />
                                  </td>
                                  <ActionCell index={i} rows={shiftRows} onAdd={() => addRow(setShiftRows, emptyShiftRow)} onRemove={(idx) => removeRow(setShiftRows, idx)} />
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Join History Tab */}
                      {activeTab === "join" && (
                        <div className="tab-pane-content table-responsive">
                          <table className="table table-bordered table-sm align-middle">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: 40 }} className="text-center">#</th>
                                <th>Join Date</th>
                                <th>Join Type</th>
                                <th>Left Date</th>
                                <th>Reason of Left</th>
                                <th style={{ width: 85 }} className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {joinRows.map((row, i) => (
                                <tr key={i}>
                                  <td className="align-middle text-center">{i + 1}</td>
                                  <td><input type="date" className="form-control form-control-sm" value={row.JoinDate ? row.JoinDate.split("T")[0] : ""} onChange={(e) => changeRow(setJoinRows, i, "JoinDate", e.target.value)} /></td>
                                  <td>
                                    <select className="form-control form-control-sm" value={row.F_JoinTypeMaster} onChange={(e) => changeRow(setJoinRows, i, "F_JoinTypeMaster", e.target.value)}>
                                      <option value="">-- Select --</option>
                                      {state.JoinTypeMaster.map((j) => <option key={j.Id} value={j.Id}>{j.Name}</option>)}
                                    </select>
                                  </td>
                                  <td><input type="date" className="form-control form-control-sm" value={row.LeftDate ? row.LeftDate.split("T")[0] : ""} onChange={(e) => changeRow(setJoinRows, i, "LeftDate", e.target.value)} /></td>
                                  <td><input type="text" className="form-control form-control-sm" value={row.ReasonOfLeft || ""} onChange={(e) => changeRow(setJoinRows, i, "ReasonOfLeft", e.target.value)} /></td>
                                  <ActionCell index={i} rows={joinRows} onAdd={() => addRow(setJoinRows, emptyJoinRow)} onRemove={(idx) => removeRow(setJoinRows, idx)} />
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Allowances Tab */}
                      {activeTab === "allowances" && (() => {
                        const sortedAllowanceMaster = [...(state.AllowanceMaster || [])].sort((a, b) => (a.Ordering || 0) - (b.Ordering || 0));

                        const groups = {};
                        allowanceRows.forEach((row, i) => {
                          const key = row.F_AllowanceMaster || `new_${i}`;
                          if (!groups[key]) groups[key] = [];
                          groups[key].push({ row, index: i });
                        });
                        Object.values(groups).forEach(g => g.sort((a, b) => (a.row.EffectiveFrom || "").localeCompare(b.row.EffectiveFrom || "")));

                        const sortedEntries = Object.entries(groups).sort(([keyA], [keyB]) => {
                          const indexA = sortedAllowanceMaster.findIndex(a => String(a.Id) === String(keyA));
                          const indexB = sortedAllowanceMaster.findIndex(a => String(a.Id) === String(keyB));
                          if (indexA === -1 && indexB === -1) return 0;
                          if (indexA === -1) return 1;
                          if (indexB === -1) return -1;
                          return indexA - indexB;
                        });

                        return (
                          <div className="tab-pane-content">
                            {sortedEntries.map(([key, entries]) => {
                              const total = entries.length;
                              const viewIdx = allowanceViewIndex[key] ?? (total - 1);
                              const { row, index: i } = entries[viewIdx] || entries[total - 1];
                              const allowanceName = sortedAllowanceMaster.find(a => String(a.Id) === String(row.F_AllowanceMaster))?.Name || `Allowance`;
                              const isExpanded = expandedAllowances[key] !== undefined ? !!expandedAllowances[key] : true;

                              return (
                                <div key={key} className="card mb-1">
                                  <div className="card-header py-1 px-2 d-flex justify-content-between align-items-center bg-light">
                                    <div className="d-flex align-items-center gap-2">
                                      <button type="button" className="btn btn-sm py-0 px-1" style={{ fontSize: "10px", lineHeight: 1 }}
                                        onClick={() => setExpandedAllowances(prev => ({ ...prev, [key]: !isExpanded }))}>
                                        {isExpanded ? "▼" : "▶"}
                                      </button>
                                      <strong style={{ fontSize: "11.5px" }}>{allowanceName}</strong>
                                      {!isExpanded && (
                                        <small className="text-muted ms-2" style={{ fontSize: "10.5px" }}>
                                          Rate: {row.Amount || "-"} | {row.Calculation || "-"} | {row.EffectiveFrom ? row.EffectiveFrom.split("T")[0] : "-"}
                                          {row.IsPFApply ? " | PF" : ""}{row.IsESIApply ? " | ESI" : ""}{row.IsApplicable ? " | Active" : ""}
                                        </small>
                                      )}
                                      {total > 1 && (
                                        <span className="d-flex align-items-center gap-1 ms-2">
                                          <button type="button" className="btn btn-xs btn-outline-secondary py-0 px-1" disabled={viewIdx === 0}
                                            onClick={() => setAllowanceViewIndex(prev => ({ ...prev, [key]: viewIdx - 1 }))}>
                                            ◀
                                          </button>
                                          <small className="text-muted">{viewIdx + 1}/{total}</small>
                                          <button type="button" className="btn btn-xs btn-outline-secondary py-0 px-1" disabled={viewIdx === total - 1}
                                            onClick={() => setAllowanceViewIndex(prev => ({ ...prev, [key]: viewIdx + 1 }))}>
                                            ▶
                                          </button>
                                        </span>
                                      )}
                                    </div>
                                    <ActionCell index={i} rows={allowanceRows} onAdd={() => addRow(setAllowanceRows, emptyAllowanceRow)} onRemove={(idx) => removeRow(setAllowanceRows, idx)} />
                                  </div>
                                  {isExpanded && (
                                  <div className="card-body py-1 px-2">
                                    <div className="row gx-2 gy-1 mb-1 align-items-center">
                                      <div className="col-md-4">
                                        <label className="text-label fw-bold mb-0">NAME</label>
                                        <select
                                          className="form-control form-control-sm"
                                          value={row.F_AllowanceMaster}
                                          onChange={(e) => {
                                            const newMasterId = e.target.value;
                                            changeRow(setAllowanceRows, i, "F_AllowanceMaster", newMasterId);
                                            if (newMasterId) {
                                              setExpandedAllowances(prev => ({
                                                ...prev,
                                                [key]: true,
                                                [newMasterId]: true,
                                                [`new_${i}`]: true
                                              }));
                                            }
                                          }}
                                        >
                                          <option value="">-- Select --</option>
                                          {sortedAllowanceMaster.map((a) => <option key={a.Id} value={a.Id}>{a.Name}</option>)}
                                        </select>
                                      </div>
                                      <div className="col-md-3">
                                        <label className="text-label fw-bold mb-0">DATE</label>
                                        <input type="date" className="form-control form-control-sm" value={row.EffectiveFrom ? row.EffectiveFrom.split("T")[0] : ""} onChange={(e) => changeRow(setAllowanceRows, i, "EffectiveFrom", e.target.value)} />
                                      </div>
                                      <div className="col-md-2">
                                        <label className="text-label fw-bold mb-0">RATE</label>
                                        <input type="number" step="0.01" className="form-control form-control-sm" value={row.Amount} onChange={(e) => changeRow(setAllowanceRows, i, "Amount", e.target.value)} />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="text-label fw-bold mb-0">CALCULATION</label>
                                        <div className="d-flex gap-2 mt-1">
                                          {["MANUAL", "PERCENTAGE", "DAILY"].map((opt) => (
                                            <div key={opt} className="form-check form-check-inline mb-0">
                                              <input className="form-check-input" type="radio" name={`calc_${i}`} id={`calc_${i}_${opt}`} value={opt} checked={row.Calculation === opt} onChange={(e) => changeAllowanceCalculation(i, e.target.value)} />
                                              <label className="form-check-label" htmlFor={`calc_${i}_${opt}`}>{opt}</label>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center gap-3 mt-1 pt-1 border-top">
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`onGross_${i}`} checked={!!row.IsOnGross} onChange={(e) => changeRow(setAllowanceRows, i, "IsOnGross", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`onGross_${i}`}>ON GROSS</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`pfApply_${i}`} checked={!!row.IsPFApply} onChange={(e) => changeRow(setAllowanceRows, i, "IsPFApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`pfApply_${i}`}>PF APPLY</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`esiApply_${i}`} checked={!!row.IsESIApply} onChange={(e) => changeRow(setAllowanceRows, i, "IsESIApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`esiApply_${i}`}>ESI APPLY</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`roundOff_${i}`} checked={!!row.IsRoundOffApply} onChange={(e) => changeRow(setAllowanceRows, i, "IsRoundOffApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`roundOff_${i}`}>ROUNDED OFF</label></div>
                                      {row.Calculation === "MANUAL" && (
                                        <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`workDays_${i}`} checked={!!row.IsWorkingDaysApply} onChange={(e) => changeRow(setAllowanceRows, i, "IsWorkingDaysApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`workDays_${i}`}>WORKING DAYS APPLY</label></div>
                                      )}
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`applicable_${i}`} checked={!!row.IsApplicable} onChange={(e) => changeRow(setAllowanceRows, i, "IsApplicable", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`applicable_${i}`}>APPLICABLE</label></div>
                                    </div>
                                  </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Deductions Tab */}
                      {activeTab === "deductions" && (() => {
                        const groups = {};
                        deductionRows.forEach((row, i) => {
                          const key = row.F_DeductionMaster || `new_${i}`;
                          if (!groups[key]) groups[key] = [];
                          groups[key].push({ row, index: i });
                        });
                        Object.values(groups).forEach(g => g.sort((a, b) => (a.row.EffectiveFrom || "").localeCompare(b.row.EffectiveFrom || "")));

                        return (
                          <div className="tab-pane-content">
                            <div className="mb-1 text-end">
                              <button type="button" className="btn btn-success btn-xs" onClick={() => addRow(setDeductionRows, emptyDeductionRow)}>
                                <i className="fa fa-plus" /> Add Deduction
                              </button>
                            </div>
                            {Object.entries(groups).map(([key, entries]) => {
                              const total = entries.length;
                              const viewIdx = deductionViewIndex[key] ?? (total - 1);
                              const { row, index: i } = entries[viewIdx] || entries[total - 1];
                              const deductionName = state.DeductionMaster.find(a => String(a.Id) === String(row.F_DeductionMaster))?.Name || "New Deduction";
                              const isExpanded = expandedDeductions[key] !== undefined ? !!expandedDeductions[key] : true;

                              return (
                                <div key={key} className="card mb-1">
                                  <div className="card-header py-1 px-2 d-flex justify-content-between align-items-center bg-light">
                                    <div className="d-flex align-items-center gap-2">
                                      <button type="button" className="btn btn-sm py-0 px-1" style={{ fontSize: "10px", lineHeight: 1 }}
                                        onClick={() => setExpandedDeductions(prev => ({ ...prev, [key]: !isExpanded }))}>
                                        {isExpanded ? "▼" : "▶"}
                                      </button>
                                      <strong style={{ fontSize: "11.5px" }}>{deductionName}</strong>
                                      {!isExpanded && (
                                        <small className="text-muted ms-2" style={{ fontSize: "10.5px" }}>
                                          Rate: {row.Amount || "-"} | {row.Calculation || "-"} | {row.EffectiveFrom ? row.EffectiveFrom.split("T")[0] : "-"}
                                          {row.IsPFApply ? " | PF" : ""}{row.IsESIApply ? " | ESI" : ""}{row.IsApplicable ? " | Active" : ""}
                                        </small>
                                      )}
                                      {total > 1 && (
                                        <span className="d-flex align-items-center gap-1 ms-2">
                                          <button type="button" className="btn btn-xs btn-outline-secondary py-0 px-1" disabled={viewIdx === 0}
                                            onClick={() => setDeductionViewIndex(prev => ({ ...prev, [key]: viewIdx - 1 }))}>◀</button>
                                          <small className="text-muted">{viewIdx + 1}/{total}</small>
                                          <button type="button" className="btn btn-xs btn-outline-secondary py-0 px-1" disabled={viewIdx === total - 1}
                                            onClick={() => setDeductionViewIndex(prev => ({ ...prev, [key]: viewIdx + 1 }))}>▶</button>
                                        </span>
                                      )}
                                    </div>
                                    <ActionCell index={i} rows={deductionRows} onAdd={() => addRow(setDeductionRows, emptyDeductionRow)} onRemove={(idx) => removeRow(setDeductionRows, idx)} />
                                  </div>
                                  {isExpanded && (
                                  <div className="card-body py-1 px-2">
                                    <div className="row gx-2 gy-1 mb-1 align-items-center">
                                      <div className="col-md-4">
                                        <label className="text-label fw-bold mb-0">NAME</label>
                                        <select
                                          className="form-control form-control-sm"
                                          value={row.F_DeductionMaster}
                                          onChange={(e) => {
                                            const newMasterId = e.target.value;
                                            changeRow(setDeductionRows, i, "F_DeductionMaster", newMasterId);
                                            if (newMasterId) {
                                              setExpandedDeductions(prev => ({
                                                ...prev,
                                                [key]: true,
                                                [newMasterId]: true,
                                                [`new_${i}`]: true
                                              }));
                                            }
                                          }}
                                        >
                                          <option value="">-- Select --</option>
                                          {state.DeductionMaster.map((a) => <option key={a.Id} value={a.Id}>{a.Name}</option>)}
                                        </select>
                                      </div>
                                      <div className="col-md-3">
                                        <label className="text-label fw-bold mb-0">DATE</label>
                                        <input type="date" className="form-control form-control-sm" value={row.EffectiveFrom ? row.EffectiveFrom.split("T")[0] : ""} onChange={(e) => changeRow(setDeductionRows, i, "EffectiveFrom", e.target.value)} />
                                      </div>
                                      <div className="col-md-2">
                                        <label className="text-label fw-bold mb-0">RATE</label>
                                        <input type="number" step="0.01" className="form-control form-control-sm" value={row.Amount} onChange={(e) => changeRow(setDeductionRows, i, "Amount", e.target.value)} />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="text-label fw-bold mb-0">CALCULATION</label>
                                        <div className="d-flex gap-2 mt-1">
                                          {["MANUAL", "PERCENTAGE", "DAILY"].map((opt) => (
                                            <div key={opt} className="form-check form-check-inline mb-0">
                                              <input className="form-check-input" type="radio" name={`dedcalc_${i}`} id={`dedcalc_${i}_${opt}`} value={opt} checked={row.Calculation === opt} onChange={(e) => changeDeductionCalculation(i, e.target.value)} />
                                              <label className="form-check-label" htmlFor={`dedcalc_${i}_${opt}`}>{opt}</label>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center gap-3 mt-1 pt-1 border-top">
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedonGross_${i}`} checked={!!row.IsOnGross} onChange={(e) => changeRow(setDeductionRows, i, "IsOnGross", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedonGross_${i}`}>ON GROSS</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedpfApply_${i}`} checked={!!row.IsPFApply} onChange={(e) => changeRow(setDeductionRows, i, "IsPFApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedpfApply_${i}`}>PF APPLY</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedesiApply_${i}`} checked={!!row.IsESIApply} onChange={(e) => changeRow(setDeductionRows, i, "IsESIApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedesiApply_${i}`}>ESI APPLY</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedroundOff_${i}`} checked={!!row.IsRoundOffApply} onChange={(e) => changeRow(setDeductionRows, i, "IsRoundOffApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedroundOff_${i}`}>ROUNDED OFF</label></div>
                                      {row.Calculation === "MANUAL" && (
                                        <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedworkDays_${i}`} checked={!!row.IsWorkingDaysApply} onChange={(e) => changeRow(setDeductionRows, i, "IsWorkingDaysApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedworkDays_${i}`}>WORKING DAYS APPLY</label></div>
                                      )}
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedapplicable_${i}`} checked={!!row.IsApplicable} onChange={(e) => changeRow(setDeductionRows, i, "IsApplicable", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedapplicable_${i}`}>APPLICABLE</label></div>
                                    </div>
                                  </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      </div>

                      {/* Pinned Bottom Footer Navigation */}
                      <div className="emp-footer-actions">
                        {activeTab !== tabOrder[tabOrder.length - 1].key && (
                          <button
                            type="button"
                            id="btnNextTab"
                            className="btn btn-info btn-sm"
                            onClick={() => {
                              const currentIndex = tabOrder.findIndex(t => t.key === activeTab);
                              if (currentIndex !== -1 && currentIndex < tabOrder.length - 1) {
                                setActiveTab(tabOrder[currentIndex + 1].key);
                              }
                            }}
                          >
                            Next <i className="fa fa-arrow-right ms-1" />
                          </button>
                        )}
                        <button type="submit" id="btnSubmitForm" className="btn btn-primary btn-sm" disabled={isSubmitting}>
                          {isSubmitting ? "Saving..." : "Save"}
                        </button>
                        {state.id > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete employee "${values.Name || state.id}"?`)) {
                                try {
                                  await Fn_DeleteData(dispatch, () => {}, Number(state.id), `${API_WEB_URLS.MASTER}/0/token/EmployeeMaster`);
                                  toast.success("Employee deleted successfully");
                                  navigate("/EmployeeMaster");
                                } catch (err) {
                                  console.error("Error deleting employee:", err);
                                }
                              }
                            }}
                          >
                            Delete
                          </button>
                        )}
                        <button type="button" className="btn btn-danger light btn-sm" onClick={() => navigate("/EmployeeMaster")}>
                          Cancel
                        </button>
                      </div>

                    </form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddEdit_EmployeeMaster;
